import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BookingCalendar from './BookingCalendar';
import InstructorHelpModal from './InstructorHelpModal';
import * as Styles from './styles';
import SettingsDrawer from './SettingsDrawer';
import { usePostcodeSearch } from './usePostcodeSearch';
import schoolLogo from '../school-brand.jpg';
import { DYNAMIC_VEHICLE_FRAMEWORKS, MASTER_READINESS_CHECKLIST, scoringGuide } from './constants';
import * as XLSX from 'xlsx';
import RouteStudio from './RouteStudio';

function InstructorDashMobile() {
  const navigate = useNavigate();
  const instructorId = localStorage.getItem('instructorId');
  const userRole = localStorage.getItem('userRole');

  const [learners, setLearners] = useState([]);
  const [template, setTemplate] = useState([]);
  const [instructorBookings, setInstructorBookings] = useState([]);
  const [allLogsCache, setAllLogsCache] = useState([]);
  
  const [instructorName, setInstructorName] = useState('');
  const [instructorDob, setInstructorDob] = useState('');
  const [instructorBadgeNumber, setInstructorBadgeNumber] = useState('');
  const [workStart, setWorkStart] = useState("08:00");
  const [workEnd, setWorkEnd] = useState("19:00");
  const [cancellationLockHours, setCancellationLockHours] = useState(24);
  const [lessonBufferMinutes, setLessonBufferMinutes] = useState(0); 
  const [instructorEmail, setInstructorEmail] = useState('');
  const [instructorPhone, setInstructorPhone] = useState('');
  const [instructorAddress, setInstructorAddress] = useState('');
  const [instructorPostcode, setInstructorPostcode] = useState('');
  const [hqPostcode, setHqPostcode] = useState('');
  const [travelRadius, setTravelRadius] = useState(5);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [vehicleCategories, setVehicleCategories] = useState(["Car (Manual)"]);

  const [currentDate, setCurrentDate] = useState(new Date());  
  const [calendarView, setCalendarView] = useState('week'); // 'day' | '3days' | 'week'
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [showHoursAccordion, setShowHoursAccordion] = useState(false);

  // Messages & Broadcast states
  const [dispatchMessages, setDispatchMessages] = useState([]);
  const [replyTexts, setReplyTexts] = useState({});
  const [broadcastText, setBroadcastText] = useState('');
  const [isBroadcasting, setIsBroadcasting] = useState(false);

  // Add student form states
  const [studentName, setStudentName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [trainingCategory, setTrainingCategory] = useState('Car (Manual)');

  // Calendar slot booking/blocking modal states
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [selection, setSelection] = useState(null);
  const [slotIntent, setSlotIntent] = useState('book');
  const [slotSelectedLearnerId, setSlotSelectedLearnerId] = useState('');
  const [slotDurationHours, setSlotDurationHours] = useState(1);
  const [slotPickupType, setSlotPickupType] = useState('primary');
  const [slotCustomPickup, setSlotCustomPickup] = useState('');
  const [showMonthPicker, setShowMonthPicker] = useState(false);
const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddDate, setQuickAddDate] = useState(new Date().toISOString().slice(0, 10));
  const [quickAddTime, setQuickAddTime] = useState('09:00');




  // Existing Booking Management Modal States
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [actionType, setActionType] = useState('view');
  const [newBookingDate, setNewBookingDate] = useState('');
  const [newBookingTime, setNewBookingTime] = useState('');
  const [messageText, setMessageText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showLogLessonModal, setShowLogLessonModal] = useState(false);

  const [historicalLogs, setHistoricalLogs] = useState([]);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showDirectMessageModal, setShowDirectMessageModal] = useState(false);
  const [targetMessageStudent, setTargetMessageStudent] = useState(null);
  const [directMessageText, setDirectMessageText] = useState('');
  const [adminMessages, setAdminMessages] = useState([]);

  const [customLogNotes, setCustomLogNotes] = useState('');
  const [nextStepsPlan, setNextStepsPlan] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [logDate, setLogDate] = useState(new Date().toISOString().slice(0, 10));
  const [logDuration, setLogDuration] = useState(60);
  const [logProgressScore, setLogProgressScore] = useState(3);
  const [readinessChecklist, setReadinessChecklist] = useState([]);
  const [modalActiveTab, setModalActiveTab] = useState('matrix');
  const [activeMobileView, setActiveMobileView] = useState('menu');
  const [hasChanges, setHasChanges] = useState(false);

  const {
    nearbyAreas,
    findNearbyAreas,
    latitude: postcodeLatitude,
    longitude: postcodeLongitude
  } = usePostcodeSearch();

  const [summaryCues] = useState([
    "✨ Drove Well",
    "🛡️ Safety Great",
    "🎯 Good Car Control",
    "⚠️ Needs Observation",
    "💡 Progressing Well"
  ]);

  const [nextStepsCues] = useState([
    "🔄 Target Reversing",
    "🚦 Target Junctions",
    "🅿️ Bay Parking Focus",
    "🛣️ Dual Carriageway Practice",
    "🛑 Emergency Stop Refresher"
  ]);

  useEffect(() => {
    if (!instructorId || userRole !== 'instructor') {
      navigate('/');
    }
  }, [instructorId, userRole, navigate]);

  const fetchData = useCallback(async () => {
    if (!instructorId) return;
    try {
      const [lRes, iRes, bRes, logsRes, mRes] = await Promise.all([
        fetch(`/api/learners?instructorId=${instructorId}`, { cache: 'no-store' }),
        fetch(`/api/instructors/${instructorId}`),
        fetch(`/api/bookings?instructorId=${instructorId}`),
        fetch(`/api/instructor-logs/${instructorId}`, { cache: 'no-store' }),
        fetch(`/api/messages/instructor/${instructorId}`, { cache: 'no-store' })
      ]);

      if (lRes.ok) {
        const lData = await lRes.json();
        setLearners(lData.map(l => ({
          ...l,
          training_category: l.training_category || "Car (Manual)",
          skills: typeof l.skills === 'string' ? JSON.parse(l.skills) : (l.skills || {})
        })));
      }
      if (logsRes.ok) setAllLogsCache(await logsRes.json());
      if (bRes.ok) setInstructorBookings(await bRes.json());
      if (mRes.ok) {
        const mData = await mRes.json();
        if (Array.isArray(mData)) {
          const sorted = mData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setDispatchMessages(sorted.filter(m => m.learner_id)); 
          setAdminMessages(sorted.filter(m => !m.learner_id));  
        } else {
          setDispatchMessages([]);
          setAdminMessages([]);
        }
      }
      if (iRes.ok) {
        const iData = await iRes.json();
        setInstructorName(iData.name || '');
        setInstructorDob(iData.date_of_birth || '');
        setInstructorBadgeNumber(iData.dvsa_badge_number || '');
        setWorkStart(iData.work_start_time?.substring(0, 5) || "08:00");
        setWorkEnd(iData.work_end_time?.substring(0, 5) || "19:00");
        setCancellationLockHours(iData.cancellation_lock_hours ?? 24);
        setLessonBufferMinutes(iData.lesson_buffer_minutes !== undefined && iData.lesson_buffer_minutes !== null ? parseInt(iData.lesson_buffer_minutes, 10) : 0); 
        setInstructorEmail(iData.email || '');
        setInstructorPhone(iData.phone || '');
        setInstructorAddress(iData.address || '');
        setInstructorPostcode(iData.postcode || '');
        setHqPostcode(iData.postcode || '');
        setTravelRadius(iData.travel_radius_miles || 5);

        try {
          const parsedAreas = typeof iData.selected_areas === 'string' 
            ? JSON.parse(iData.selected_areas) 
            : (Array.isArray(iData.selected_areas) ? iData.selected_areas : []);
          setSelectedAreas(parsedAreas);
        } catch (e) { setSelectedAreas([]); }

        try {
          const transmission = typeof iData.transmission_type === 'string' 
            ? JSON.parse(iData.transmission_type) 
            : (iData.transmission_type || ["Car (Manual)"]);
          
          const cats = Array.isArray(transmission) ? transmission : [transmission];
          setVehicleCategories(cats);
          
          if (cats.length > 0) {
              setTrainingCategory(cats[0]);
          }
        } catch (e) { 
          setVehicleCategories(["Car (Manual)"]); 
        }

        try {
          const t = typeof iData.calendar_template === 'string' ? JSON.parse(iData.calendar_template) : (iData.calendar_template || []);
          setTemplate(Array.isArray(t) ? t.map(b => ({ ...b, date: new Date(b.date) })) : []);
        } catch (e) { setTemplate([]); }
      }
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [instructorId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const timeLabels = useMemo(() => [
    "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", 
    "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", 
    "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", 
    "17:00", "17:30", "18:00"
  ], []);

  // 🔑 Track last active login timestamp
  useEffect(() => {
    if (!localStorage.getItem('instructorLastLogin')) {
      localStorage.setItem('instructorLastLogin', new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }
  }, []);
  const lastLoginTime = localStorage.getItem('instructorLastLogin') || 'Today';

  // 📅 Next 7 Days Upcoming Lessons Feed
  const upcomingLessonsNext7Days = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const nextWeek = new Date(now);
    nextWeek.setDate(now.getDate() + 7);

    return instructorBookings
      .filter(b => {
        if (b.status === 'cancelled') return false;
        const bDate = new Date(b.booking_date || b.date);
        return bDate >= now && bDate <= nextWeek;
      })
      .sort((a, b) => new Date(a.booking_date || a.date) - new Date(b.booking_date || b.date));
  }, [instructorBookings]);

  const getDateOnly = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return [
        d.getFullYear(),
        String(d.getMonth() + 1).padStart(2, '0'),
        String(d.getDate()).padStart(2, '0')
      ].join('-');
    }
    return null;
  };


// 🇬🇧 UK Date Formatter Helper (converts YYYY-MM-DD to DD/MM/YYYY)
  const formatUKDate = (dateVal) => {
    if (!dateVal) return 'N/A';
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return String(dateVal);
    return d.toLocaleDateString('en-GB'); // Formats as DD/MM/YYYY
  };

  // ⚠️ Past Lessons from the last 7 days missing a log
  const unloggedPastLessons = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const pastWeek = new Date(now);
    pastWeek.setDate(now.getDate() - 7);

    return instructorBookings
      .filter(b => {
        if (b.status === 'cancelled') return false;
        const bDate = new Date(b.booking_date || b.date);
        if (bDate >= now || bDate < pastWeek) return false;

        const bDateStr = getDateOnly(b.booking_date || b.date);
        const hasLog = allLogsCache.some(log => 
          parseInt(log.learner_id, 10) === parseInt(b.learner_id, 10) &&
          log.lesson_date?.slice(0, 10) === bDateStr
        );
        return !hasLog;
      })
      .sort((a, b) => new Date(b.booking_date || b.date) - new Date(a.booking_date || a.date));
  }, [instructorBookings, allLogsCache, getDateOnly]);

  // 🔑 Dynamically generate visible dates based on Day, 3-Days, or Week view
  const displayedDates = useMemo(() => {
    const dates = [];
    const base = new Date(currentDate);
    base.setHours(0, 0, 0, 0);

    if (calendarView === 'day') {
      dates.push(new Date(base));
    } else if (calendarView === '3days') {
      for (let i = 0; i < 3; i++) {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        dates.push(d);
      }
    } else {
      const day = base.getDay();
      const diff = base.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(base.setDate(diff));
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        dates.push(d);
      }
    }
    return dates;
  }, [currentDate, calendarView]);

  // 🔑 Calculate week gone (past 7 days) and week ahead (next 7 days) analytics
  const weeklyAnalytics = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const pastWeekStart = new Date(now);
    pastWeekStart.setDate(now.getDate() - 7);

    const nextWeekEnd = new Date(now);
    nextWeekEnd.setDate(now.getDate() + 7);

    const pastLogs = allLogsCache.filter(log => {
      const logDate = new Date(log.lesson_date);
      return logDate >= pastWeekStart && logDate < now;
    });
    const pastHoursCompleted = pastLogs.reduce((sum, log) => sum + (log.duration_minutes ? log.duration_minutes / 60 : 1), 0);

    const upcomingBookings = instructorBookings.filter(b => {
      if (b.status === 'cancelled') return false;
      const bDate = new Date(b.booking_date || b.date);
      return bDate >= now && bDate <= nextWeekEnd;
    });
    const upcomingHours = upcomingBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

    return {
      pastCount: pastLogs.length,
      pastHours: Math.round(pastHoursCompleted),
      upcomingCount: upcomingBookings.length,
      upcomingHours: Math.round(upcomingHours),
      upcomingList: upcomingBookings.slice(0, 3)
    };
  }, [allLogsCache, instructorBookings]);

  const handlePrevPeriod = () => {
    const d = new Date(currentDate);
    if (calendarView === 'day') d.setDate(d.getDate() - 1);
    else if (calendarView === '3days') d.setDate(d.getDate() - 3);
    else d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };

  const handleNextPeriod = () => {
    const d = new Date(currentDate);
    if (calendarView === 'day') d.setDate(d.getDate() + 1);
    else if (calendarView === '3days') d.setDate(d.getDate() + 3);
    else d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };

  const calculateProgress = (skills = {}) => {
    const targetFramework = DYNAMIC_VEHICLE_FRAMEWORKS[selectedStudent?.training_category || "Car (Manual)"];
    const totalSkillsCount = Object.values(targetFramework || {}).flat().length || 1;
    const safeSkills = skills || {};
    const totalAccumulatedScore = Object.values(safeSkills).reduce((sum, current) => sum + current, 0);
    return Math.round((totalAccumulatedScore / (totalSkillsCount * 5)) * 100) || 0;
  };

  const triggerMasterConfigurationSave = async (drawerPayload = {}) => {
    const [newStartH] = workStart.split(':').map(Number);
    const [newEndH] = workEnd.split(':').map(Number);

    const hasConflictingBooking = instructorBookings?.some(b => {
      if (b.status === 'cancelled') return false;
      const [bStartH] = String(b.booking_time || '00:00').split(':').map(Number);
      const bDuration = parseInt(b.duration || 1, 10);
      const bEndH = bStartH + bDuration;

      return bStartH < newStartH || bEndH > newEndH;
    });

    if (hasConflictingBooking) {
      alert("❌ Cannot update working hours: You have confirmed student lessons scheduled outside of these new hours.");
      fetchData(); 
      return;
    }

    try {
      const res = await fetch(`/api/instructors/${instructorId}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: instructorEmail,
          phone: instructorPhone,
          address: instructorAddress,
          postcode: instructorPostcode || hqPostcode,
          travel_radius_miles: parseInt(travelRadius, 10),
          work_start_time: workStart,
          work_end_time: workEnd,
          cancellation_lock_hours: cancellationLockHours,
          lesson_buffer_minutes: parseInt(lessonBufferMinutes, 10), 
          calendar_template: template,
          transmission_type: vehicleCategories,
          selected_areas: selectedAreas,
          date_of_birth: instructorDob,
          dvsa_badge_number: instructorBadgeNumber,
          name: drawerPayload.name || instructorName,
          newPassword: drawerPayload.newPassword || null,
          latitude: drawerPayload.latitude !== undefined ? drawerPayload.latitude : null,
          longitude: drawerPayload.longitude !== undefined ? drawerPayload.longitude : null,
          ...drawerPayload
        })
      });
      if (res.ok) {
        alert("🎉 Settings, vehicle classes, and coverage areas saved successfully!");
        setShowSettingsDrawer(false);       
        fetchData();
      } else {
        alert("❌ Failed to save settings.");
        fetchData();
      }
    } catch (err) {
      alert("❌ Server connection error while saving settings.");
      fetchData();
    }
  };

  const handleSendInstructorReply = async (learnerId) => {
    const text = replyTexts[learnerId];
    if (!text || !text.trim()) return;
    try {
      const res = await fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructor_id: parseInt(instructorId, 10),
          learner_id: parseInt(learnerId, 10),
          sender_role: 'instructor',
          message_text: text.trim()
        })
      });
      if (res.ok) {
        setReplyTexts(prev => ({ ...prev, [learnerId]: '' }));
        fetchData();
        alert("✅ Reply sent!");
      }
    } catch (err) {
      alert("❌ Failed to send reply.");
    }
  };

  const handleSendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastText.trim()) return;
    if (!window.confirm("Send this announcement message to ALL your registered students?")) return;

    setIsBroadcasting(true);
    try {
      const res = await fetch('/api/messages/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructor_id: parseInt(instructorId, 10),
          message_text: broadcastText.trim()
        })
      });
      if (res.ok) {
        alert("🎉 Broadcast announcement sent successfully!");
        setBroadcastText('');
      }
    } catch (err) {
      alert("❌ Failed to send broadcast.");
    } finally {
      setIsBroadcasting(false);
    }
  };

  const handleSendDirectMessage = async () => {
    if (!directMessageText.trim() || !targetMessageStudent) return;
    try {
      const res = await fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructor_id: parseInt(instructorId, 10),
          learner_id: parseInt(targetMessageStudent.id, 10),
          sender_role: 'instructor',
          message_text: directMessageText.trim()
        })
      });

      if (res.ok) {
        alert(`✉️ Direct message successfully sent to ${targetMessageStudent.name}!`);
        setDirectMessageText('');
        setShowDirectMessageModal(false);
        setTargetMessageStudent(null);
      } else {
        alert("❌ Failed to send direct message.");
      }
    } catch (err) {
      alert("❌ Server connection error.");
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/learners', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ 
          name: studentName, 
          email: studentEmail, 
          instructor_id: instructorId,
          training_category: trainingCategory 
        }) 
      });
      if (response.ok) {
        alert("🎉 Student added and secure activation link sent!");
        setStudentName(''); 
        setStudentEmail('');
        fetchData(); 
      } else {
        const errData = await response.json();
        alert(`❌ Error: ${errData.error || "Failed to add student"}`);
      }
    } catch (err) {
      alert("❌ Server connection error.");
    }
  };

  const handleOpenTrackingPortal = async (studentOrBooking, bookedDuration = 60, specificDate = null) => {
    const masterStudent = learners.find(l => 
      l.name?.toLowerCase().trim() === studentOrBooking.name?.toLowerCase().trim() ||
      parseInt(l.id, 10) === parseInt(studentOrBooking.id, 10) ||
      parseInt(l.id, 10) === parseInt(studentOrBooking.learner_id, 10)
    ) || studentOrBooking;

    let isAlreadyLogged = false;
    const targetDate = specificDate || studentOrBooking.booking_date?.slice(0, 10) || new Date().toISOString().slice(0, 10);
    setLogDate(targetDate);
    setLogDuration(bookedDuration ? parseInt(bookedDuration, 10) : 60);

    setCustomLogNotes('');
    setNextStepsPlan('');
    
    try {
      const res = await fetch(`/api/lesson-logs/${masterStudent.id}`);
      if (res.ok) {
        const data = await res.json();
        setHistoricalLogs(data);
        const existingLogForDate = data.find(l => l.lesson_date?.slice(0, 10) === targetDate);
        if (existingLogForDate) {
          setCustomLogNotes(existingLogForDate.custom_notes || '');
          setNextStepsPlan(existingLogForDate.next_steps || '');
          setLogProgressScore(existingLogForDate.progress_score || 3);
          isAlreadyLogged = true;
        }
      }
    } catch (err) { 
      console.error(err); 
    }
    
    setSelectedStudent({ ...masterStudent, isReadOnlyAudit: isAlreadyLogged });
    setShowLogLessonModal(true);
  };

  const handleOpenStudentHub = async (student) => {
    let checkArray = [];
    if (student.test_readiness_checklist) {
      checkArray = typeof student.test_readiness_checklist === 'string' 
        ? JSON.parse(student.test_readiness_checklist) 
        : student.test_readiness_checklist;
    }
    setReadinessChecklist(Array.isArray(checkArray) ? checkArray : []);

    try {
      const res = await fetch(`/api/lesson-logs/${student.id}`);
      if (res.ok) {
        setHistoricalLogs(await res.json());
      }
    } catch (err) {
      console.error(err);
    }

    setHasChanges(false);
    setSelectedStudent(student);
    setShowProgressModal(true); 
  };

  // 💾 Unified Master Save Handler for the top button
  const handleUnifiedSave = async () => {
    if (!selectedStudent || !hasChanges) return;

    try {
      await Promise.all([
        fetch(`/api/learners/${selectedStudent.id}/skills`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skills: selectedStudent.skills })
        }),
        fetch(`/api/learners/${selectedStudent.id}/readiness-checklist`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ checklist: readinessChecklist })
        }),
        fetch(`/api/learners/${selectedStudent.id}/onboarding`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: selectedStudent.name,
            email: selectedStudent.email,
            phone_number: selectedStudent.phone_number || selectedStudent.phone,
            default_pickup_address: selectedStudent.default_pickup_address || selectedStudent.address
          })
        })
      ]);

      alert("🎉 Student changes saved successfully!");
      setHasChanges(false);
      fetchData();
    } catch (err) {
      alert("❌ Failed to save changes.");
    }
  };

  const handleCommitCompleteLessonReport = async () => {
    if (!selectedStudent) return;
    setIsSaving(true);
    
    const validLearner = learners.find(l => 
      l.name?.toLowerCase().trim() === selectedStudent.name?.toLowerCase().trim() ||
      parseInt(l.id, 10) === parseInt(selectedStudent.id, 10)
    );

    if (!validLearner) {
      alert(`❌ Error: Student not found in active roster.`);
      setIsSaving(false);
      return;
    }

    const secureLearnerId = validLearner.id;

    try {
      await fetch(`/api/learners/${secureLearnerId}/skills`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skills: selectedStudent.skills })
      });

      await fetch(`/api/learners/${secureLearnerId}/readiness-checklist`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checklist: readinessChecklist })
      });

      const logRes = await fetch('/api/lesson-logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructor_id: parseInt(instructorId, 10),
          learner_id: parseInt(secureLearnerId, 10),
          lesson_date: logDate || new Date().toISOString().slice(0, 10),
          duration_minutes: parseInt(logDuration, 10) || 60,
          custom_notes: customLogNotes || '',
          next_steps: nextStepsPlan || '',
          progress_score: parseInt(logProgressScore, 10) || 1
        })
      });

      if (logRes.ok) {
        alert("🎉 Lesson report, skills, and readiness targets successfully saved!");
        await fetchData(); 
        setShowLogLessonModal(false);
      } else {
        throw new Error("Failed to save lesson log");
      }
    } catch (err) {
      alert("❌ Save failed: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSlot = async (type) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selection.date < today) {
      alert("❌ Past weeks are locked and cannot be modified.");
      setShowSlotModal(false);
      return;
    }

    const dragDateStr = getDateOnly(selection.date);
    const [startH, startM] = selection.startTime.split(':').map(Number);
    const startTotalMins = (startH * 60) + (startM || 0);
    const endTotalMins = startTotalMins + Math.round(parseFloat(slotDurationHours) * 60);

    const endH = Math.floor(endTotalMins / 60).toString().padStart(2, '0');
    const endM = (endTotalMins % 60).toString().padStart(2, '0');
    const exactEndTimeStr = `${endH}:${endM}`;

    if (type === 'blocked') {
      const hasBookingConflict = instructorBookings?.some(b => {
        const bDate = getDateOnly(b.booking_date || b.date);
        if (bDate !== dragDateStr || b.status === 'cancelled') return false;
        const [bStartH, bStartM] = String(b.booking_time || '00:00').split(':').map(Number);
        const bStartMins = bStartH * 60 + bStartM;
        const bDurMins = Math.round(parseFloat(b.duration || 1) * 60);
        const bEndMins = bStartMins + bDurMins;
        
        return startTotalMins < bEndMins && endTotalMins > bStartMins;
      });

      if (hasBookingConflict) {
        alert("❌ Cannot block time-off: There is a confirmed student booking during this time range.");
        setShowSlotModal(false);
        return;
      }
    }

    const filtered = template.filter(t => {
      if (!t || t.type !== 'blocked') return true;
      const tDateStr = getDateOnly(t.dateString || t.date);
      if (tDateStr !== dragDateStr) return true;
      
      const [tH1, tM1] = String(t.startTime).split(':').map(Number);
      const [tH2, tM2] = String(t.endTime).split(':').map(Number);
      const tStartMins = tH1 * 60 + tM1;
      const tEndMins = tH2 * 60 + tM2;

      return !(startTotalMins < tEndMins && endTotalMins > tStartMins);
    });
    
    let finalTemplate = filtered;
    if (type === 'blocked') {
      const newBlock = {
        date: selection.date,
        dateString: dragDateStr,
        startTime: selection.startTime,
        endTime: exactEndTimeStr,
        type: 'blocked',
        id: Math.random()
      };
      finalTemplate = [...filtered, newBlock];
    }

    setTemplate(finalTemplate);
    setShowSlotModal(false);
    setSelection(null);

    try {
      await fetch(`/api/instructors/${instructorId}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: instructorEmail,
          phone: instructorPhone,
          address: instructorAddress,
          postcode: instructorPostcode || hqPostcode,
          travel_radius_miles: parseInt(travelRadius, 10),
          work_start_time: workStart,
          work_end_time: workEnd,
          cancellation_lock_hours: cancellationLockHours,
          calendar_template: finalTemplate,
          transmission_type: vehicleCategories,
          selected_areas: selectedAreas,
          date_of_birth: instructorDob,
          dvsa_badge_number: instructorBadgeNumber
        })
      });
      fetchData();
    } catch (err) {
      console.error("Error saving slot block:", err);
    }
  };

  const handleRemoveBlock = async (targetDate, targetTime) => {
    const targetDateStr = getDateOnly(targetDate);
    const updatedTemplate = template.filter(t => {
      if (!t || t.type !== 'blocked') return true;
      const tDateStr = getDateOnly(t.dateString || t.date);
      if (tDateStr !== targetDateStr) return true;
      return t.startTime !== targetTime;
    });

    setTemplate(updatedTemplate);

    try {
      await fetch(`/api/instructors/${instructorId}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: instructorEmail,
          phone: instructorPhone,
          address: instructorAddress,
          postcode: instructorPostcode || hqPostcode,
          travel_radius_miles: parseInt(travelRadius, 10),
          work_start_time: workStart,
          work_end_time: workEnd,
          cancellation_lock_hours: cancellationLockHours,
          calendar_template: updatedTemplate,
          transmission_type: vehicleCategories,
          selected_areas: selectedAreas,
          date_of_birth: instructorDob,
          dvsa_badge_number: instructorBadgeNumber
        })
      });
      fetchData();
    } catch (err) {
      console.error("Error removing block:", err);
    }
  };

  const handleDirectInstructorBooking = async () => {
    if (!slotSelectedLearnerId || !selection) {
      alert("Please select a student to book.");
      return;
    }

    const dragDateStr = getDateOnly(selection.date);
    const [startH, startM] = selection.startTime.split(':').map(Number);
    const startTotalMins = (startH * 60) + (startM || 0);
    const endTotalMins = startTotalMins + (Math.round(slotDurationHours) * 60);

    const hasBookingConflict = instructorBookings?.some(b => {
      const bDate = getDateOnly(b.booking_date || b.date);
      if (bDate !== dragDateStr || b.status === 'cancelled') return false;
      const [bStartH, bStartM] = String(b.booking_time || '00:00').split(':').map(Number);
      const bStartMins = bStartH * 60 + bStartM;
      const bDurMins = Math.round(parseFloat(b.duration || 1) * 60);
      const bEndMins = bStartMins + bDurMins;
      
      return startTotalMins < bEndMins && endTotalMins > bStartMins;
    });

    const hasBlockConflict = template?.some(t => {
      if (!t || t.type !== 'blocked') return false;
      const tDateStr = getDateOnly(t.dateString || t.date);
      if (tDateStr !== dragDateStr) return false;
      const [tStartH, tStartM] = String(t.startTime || '00:00').split(':').map(Number);
      const tStartMins = tStartH * 60 + tStartM;
      const [tEndH, tEndM] = String(t.endTime || '00:00').split(':').map(Number);
      const tEndMins = tEndH * 60 + tEndM;

      return startTotalMins < tEndMins && endTotalMins > tStartMins;
    });

    if (hasBookingConflict || hasBlockConflict) {
      alert("❌ Cannot book: There is already a confirmed lesson or time-off block during this time range.");
      setShowSlotModal(false);
      return;
    }

    const targetLearner = learners.find(l => parseInt(l.id, 10) === parseInt(slotSelectedLearnerId, 10));
    const resolvedAddress = slotCustomPickup || targetLearner?.default_pickup_address || targetLearner?.address || null;

    try {
      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructor_id: instructorId,
          learner_id: slotSelectedLearnerId,
          learner_name: targetLearner?.name || 'Student',
          booking_date: selection.date.toISOString().slice(0, 10),
          booking_time: selection.startTime,
          duration: slotDurationHours,
          pickup_type: slotPickupType,
          exact_pickup_address: resolvedAddress,
          pickup_location: resolvedAddress 
        })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Failed to secure booking.");
      }
      alert("🎉 Lesson successfully booked with pickup location!");
      setShowSlotModal(false);
      setSelection(null);
      fetchData();
    } catch (err) {
      alert("❌ " + err.message);
    }
  };

  const handleMoveBooking = async () => {
    if (!newBookingDate || !newBookingTime) {
      alert("Please select a new date and time.");
      return;
    }
    setIsProcessing(true);
    try {
      const response = await fetch(`/api/bookings/${selectedBooking.id}/reschedule`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_date: newBookingDate,
          booking_time: newBookingTime,
          duration: selectedBooking.duration,
          pickup_location: selectedBooking.exact_pickup_address || selectedBooking.pickup_location
        })
      });
      if (!response.ok) throw new Error("Failed to reschedule booking");
      alert("✅ Lesson rescheduled successfully!");
      setShowManageModal(false);
      fetchData();
    } catch (err) {
      alert("❌ " + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    try {
      const response = await fetch(`/api/bookings/${bookingId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error("Failed to cancel booking");
      alert("🗑️ Booking cancelled!");
      setShowManageModal(false);
      fetchData();
    } catch (err) {
      alert("❌ " + err.message);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedBooking) return;
    try {
      await fetch('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instructor_id: parseInt(instructorId, 10),
          learner_id: parseInt(selectedBooking.learner_id, 10),
          sender_role: 'instructor',
          message_text: messageText.trim()
        })
      });
      alert(`✉️ Message sent to ${selectedBooking.learner_name}!`);
      setMessageText('');
      setActionType('view');
    } catch (err) {
      alert("❌ Failed to send message.");
    }
  };

  // 🖨️ SEPARATE REPORT 1: Individual Student Report
  const handleGeneratePrintReport = () => {
    if (!selectedStudent) return;

    const printWindow = window.open('', '_blank');
    const studentFramework = DYNAMIC_VEHICLE_FRAMEWORKS[selectedStudent.training_category || "Car (Manual)"] || {};
    const progressPercent = calculateProgress(selectedStudent.skills);

    const studentLogs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(selectedStudent.id, 10));
    const doneMinutes = studentLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
    const doneHours = Math.round(doneMinutes / 60);

    const todayStr = new Date().toISOString().slice(0, 10);
    const studentBookings = instructorBookings.filter(b => 
        (parseInt(b.learner_id, 10) === parseInt(selectedStudent.id, 10) || b.learner_name?.toLowerCase() === selectedStudent.name?.toLowerCase()) &&
        b.status === 'confirmed' &&
        String(b.booking_date).slice(0, 10) >= todayStr
    );
    const bookedHours = studentBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

    const totalChecklist = MASTER_READINESS_CHECKLIST.length;
    const achievedChecklist = readinessChecklist.length;
    const checklistPercent = Math.round((achievedChecklist / totalChecklist) * 100) || 0;
    const overallReadiness = Math.round((progressPercent + checklistPercent) / 2);

    let statusBadgeBg = '#ef4444';
    let statusText = 'Foundation Stage (Needs Development)';
    if (overallReadiness >= 80) {
        statusBadgeBg = '#22c55e';
        statusText = '🌟 Test Ready / Exam Standard';
    } else if (overallReadiness >= 50) {
        statusBadgeBg = '#eab308';
        statusText = '📈 Intermediate Progress';
    }

    const getTrafficLightColor = (percent) => {
        if (percent >= 80) return '#22c55e';
        if (percent >= 50) return '#eab308';
        return '#ef4444';
    };

    const getDonutSvg = (percent, size = 88, stroke = 9) => {
        const radius = 35;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (percent / 100) * circumference;
        const color = getTrafficLightColor(percent);

        return `
            <svg width="${size}" height="${size}" viewBox="0 0 100 100" style="transform: rotate(-90deg);">
                <circle cx="50" cy="50" r="${radius}" stroke="#e2e8f0" stroke-width="${stroke}" fill="transparent" />
                <circle cx="50" cy="50" r="${radius}" stroke="${color}" stroke-width="${stroke}" stroke-dasharray="${circumference}" stroke-dashoffset="${strokeDashoffset}" stroke-linecap="round" fill="transparent" />
            </svg>
        `;
    };

    const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Student Progress Summary - ${selectedStudent.name}</title>
            <style>
                body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; padding: 25px; line-height: 1.4; max-width: 850px; margin: 0 auto; background: #fff; }
                .report-header { display: flex; justify-content: space-between; border-bottom: 3px solid #0066cc; padding-bottom: 12px; margin-bottom: 20px; }
                h1 { color: #0066cc; margin: 0 0 3px 0; font-size: 20px; }
                .card { background: #f8f9fa; border: 1px solid #cbd5e0; border-radius: 8px; padding: 15px; margin-bottom: 15px; }
                .meta-grid { display: flex; gap: 10px; font-size: 12px; margin-top: 10px; }
                .meta-item { flex: 1; background: #ffffff; padding: 10px; border-radius: 6px; border: 1px solid #e2e8f0; text-align: center; }
                .charts-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-top: 12px; }
                .chart-card { background: #ffffff; border: 1px solid #cbd5e0; border-radius: 8px; padding: 15px 10px; text-align: center; }
                .donut-wrapper { position: relative; width: 88px; height: 88px; margin: 0 auto 8px auto; }
                .donut-text { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-weight: bold; font-size: 14px; color: #1e293b; }
                .badge { display: inline-block; padding: 3px 8px; font-size: 11px; font-weight: bold; border-radius: 4px; background: #e2e8f0; }
                .passed { background: #22c55e; color: #fff; }
                .legend { display: flex; justify-content: center; gap: 25px; font-size: 11px; margin-top: 8px; font-weight: bold; color: #334155; }
                .legend-item { display: flex; align-items: center; gap: 6px; }
                .dot { width: 11px; height: 11px; border-radius: 50%; display: inline-block; }
                .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #64748b; border-top: 1px solid #cbd5e0; padding-top: 8px; }
                @media print {
                    body { padding: 10px; }
                    button { display: none; }
                    .charts-grid { page-break-inside: avoid; }
                }
            </style>
        </head>
        <body>
            <div class="report-header">
                <div>
                    <h1>🚗 Independent Driving School</h1>
                    <p style="margin: 0; color: #64748b; font-size: 12px;">Official Student Training Summary & Performance Report</p>
                </div>
                <div style="text-align: right;">
                    <p style="margin: 0; font-weight: bold; font-size: 14px;">${selectedStudent.name}</p>
                    <p style="margin: 2px 0 0 0; font-size: 11px; color: #64748b;">Generated: ${new Date().toLocaleDateString('en-GB')}</p>
                </div>
            </div>

            <div class="card">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                    <h3 style="margin: 0; color: #0066cc; font-size: 15px;">📊 Training Status & Volume Overview</h3>
                    <span style="padding: 3px 10px; background: ${statusBadgeBg}; color: #ffffff; border-radius: 6px; font-size: 11px; font-weight: bold;">${statusText}</span>
                </div>
                <div class="meta-grid">
                    <div class="meta-item">
                        <span style="color: #64748b; font-weight: bold; display: block; font-size: 9px; margin-bottom: 2px;">CATEGORY</span>
                        <strong style="font-size: 11px;">${selectedStudent.training_category || "Car (Manual)"}</strong>
                    </div>
                    <div class="meta-item">
                        <span style="color: #64748b; font-weight: bold; display: block; font-size: 9px; margin-bottom: 2px;">STATUS</span>
                        <span class="badge ${selectedStudent.status === 'Passed' ? 'passed' : ''}">${selectedStudent.status || 'Beginner'}</span>
                    </div>
                    <div class="meta-item">
                        <span style="color: #64748b; font-weight: bold; display: block; font-size: 9px; margin-bottom: 2px;">COMPLETED</span>
                        <strong style="color: #22c55e; font-size: 12px;">${doneHours}h Done</strong>
                    </div>
                    <div class="meta-item">
                        <span style="color: #64748b; font-weight: bold; display: block; font-size: 9px; margin-bottom: 2px;">BOOKED</span>
                        <strong style="color: #0066cc; font-size: 12px;">${bookedHours}h Booked</strong>
                    </div>
                </div>
            </div>

            <div class="card" style="background: #ffffff;">
                <h3 style="margin: 0 0 10px 0; color: #0066cc; font-size: 14px; text-align: center;">🎯 Overall Mastery & Exam Gates</h3>
                <div style="display: flex; justify-content: space-around; gap: 15px;">
                    <div style="text-align: center; flex: 1;">
                        <div class="donut-wrapper" style="width: 95px; height: 95px;">
                            ${getDonutSvg(progressPercent, 95, 10)}
                            <div class="donut-text" style="font-size: 14px;">${progressPercent}%</div>
                        </div>
                        <strong style="font-size: 12px; color: #1e293b;">Total Syllabus Mastery</strong>
                    </div>
                    <div style="text-align: center; flex: 1;">
                        <div class="donut-wrapper" style="width: 95px; height: 95px;">
                            ${getDonutSvg(checklistPercent, 95, 10)}
                            <div class="donut-text" style="font-size: 14px;">${checklistPercent}%</div>
                        </div>
                        <strong style="font-size: 12px; color: #1e293b;">Test-Readiness Gate (${achievedChecklist}/${totalChecklist})</strong>
                    </div>
                </div>
            </div>

            <div class="card" style="background: #ffffff;">
                <h3 style="margin: 0 0 5px 0; color: #0066cc; font-size: 14px; text-align: center;">📁 6 Core Competency Category Breakdowns</h3>
                <div class="legend">
                    <span class="legend-item"><span class="dot" style="background: #22c55e;"></span> Proficient (80%+)</span>
                    <span class="legend-item"><span class="dot" style="background: #eab308;"></span> Developing (50-79%)</span>
                    <span class="legend-item"><span class="dot" style="background: #ef4444;"></span> Needs Focus (&lt;50%)</span>
                </div>

                <div class="charts-grid">
                    ${Object.entries(studentFramework).map(([category, skills]) => {
                        const totalPossible = skills.length * 5;
                        const totalEarned = skills.reduce((sum, skill) => sum + (selectedStudent?.skills?.[skill] || 1), 0);
                        const catPercent = Math.round((totalEarned / totalPossible) * 100);

                        return `
                            <div class="chart-card">
                                <div class="donut-wrapper">
                                    ${getDonutSvg(catPercent)}
                                    <div class="donut-text">${catPercent}%</div>
                                </div>
                                <strong style="font-size: 11px; color: #1e293b; display: block; line-height: 1.3; height: 28px; overflow: hidden; font-weight: 600;">${category}</strong>
                            </div>
                        `;
                    }).join('')}
                </div>
            </div>

            <div class="footer">
                <p>Independent Driving School Management Platform — Official Competency & Progress Report</p>
            </div>

            <script>
                window.onload = function() { window.print(); }
            </script>
        </body>
        </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleGenerateOverallFleetReport = () => {
    const formattedDob = instructorDob ? new Date(instructorDob).toLocaleDateString('en-GB') : 'Not Provided';
    const todayStr = new Date().toISOString().slice(0, 10);

    const totalStudents = learners.length;
    const passedCount = learners.filter(l => l.status === 'Passed').length;
    const testReadyCount = learners.filter(l => l.status === 'Test Ready').length;

    let totalCompletedHours = 0;
    let rosterBookedHoursTotal = 0;

    learners.forEach(l => {
      const studentLogs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(l.id, 10));
      const doneMinutes = studentLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
      totalCompletedHours += Math.round(doneMinutes / 60);

      const studentBookings = instructorBookings.filter(b => 
        (parseInt(b.learner_id, 10) === parseInt(l.id, 10) || b.learner_name?.toLowerCase() === l.name?.toLowerCase()) &&
        b.status === 'confirmed' &&
        String(b.booking_date).slice(0, 10) >= todayStr
      );
      rosterBookedHoursTotal += studentBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);
    });

    const grandTotalVolume = totalCompletedHours + rosterBookedHoursTotal;

    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
          <title>Executive Performance Dashboard - LessonLane</title>
          <style>
              body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; padding: 30px; line-height: 1.5; max-width: 1100px; margin: 0 auto; background: #f8fafc; }
              .report-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #e2e8f0; padding-bottom: 20px; margin-bottom: 25px; background: #ffffff; padding: 24px; border-radius: 16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02); }
              .school-title h1 { color: #0f172a; margin: 0; font-size: 24px; font-weight: 800; }
              .school-title p { margin: 4px 0 0; color: #64748b; font-size: 13px; }
              .report-meta { text-align: right; font-size: 12px; color: #475569; }
              .report-meta p { margin: 2px 0; }
              .metrics-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 25px; }
              .metric-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 18px; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
              .metric-card.primary { background: #eff6ff; border-left: 5px solid #2563eb; color: #1e40af; }
              .metric-card.success { background: #ecfdf5; border-left: 5px solid #059669; color: #065f46; }
              .metric-card.warning { background: #fffbeb; border-left: 5px solid #d97706; color: #b45309; }
              .metric-card.neutral { background: #f8fafc; border-left: 5px solid #475569; color: #334155; }
              .metric-title { font-size: 11px; text-transform: uppercase; font-weight: 700; letter-spacing: 0.6px; opacity: 0.85; }
              .metric-value { font-size: 22px; font-weight: 800; margin-top: 6px; }
              .section-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 24px; margin-bottom: 25px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02); }
              .section-title { font-size: 16px; font-weight: 800; color: #0f172a; margin-top: 0; margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
              .compliance-table { width: 100%; border-collapse: collapse; }
              .compliance-table td { border: none; padding: 8px 0; font-size: 13px; width: 50%; vertical-align: top; color: #334155; }
              table.roster { width: 100%; border-collapse: collapse; margin-top: 5px; }
              table.roster th { background: #f8fafc; color: #64748b; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 14px 16px; text-align: left; }
              table.roster td { border-bottom: 1px solid #f1f5f9; padding: 16px; font-size: 13px; color: #1e293b; vertical-align: middle; }
              table.roster tr:hover { background: #f8fafc; }
              .badge { display: inline-block; padding: 6px 14px; border-radius: 20px; font-size: 11px; font-weight: 800; text-align: center; letter-spacing: 0.3px; }
              .badge-beginner { background: #f1f5f9; color: #475569; }
              .badge-intermediate { background: #eff6ff; color: #2563eb; }
              .badge-ready { background: #fef3c7; color: #d97706; }
              .badge-passed { background: #ecfdf5; color: #059669; }
              .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 15px; }
          </style>
      </head>
      <body>
          <div class="report-header">
              <div class="school-title">
                  <h1>🚗 Independent Driving School</h1>
                  <p>Comprehensive Student Progress & Fleet Analytics</p>
              </div>
              <div class="report-meta">
                  <p><strong>Instructor:</strong> ${instructorName || 'Certified Partner'}</p>
                  <p><strong>DVSA Badge:</strong> ${instructorBadgeNumber || 'Pending'}</p>
                  <p><b>Generated:</b> ${new Date().toLocaleDateString('en-GB')}</p>
              </div>
          </div>

          <div class="metrics-grid">
              <div class="metric-card primary">
                  <div class="metric-title">Active Roster</div>
                  <div class="metric-value">${totalStudents} Students</div>
              </div>
              <div class="metric-card success">
                  <div class="metric-title">Total Teaching Volume</div>
                  <div class="metric-value">${grandTotalVolume} Hours</div>
                  <div style="font-size: 10px; opacity: 0.85; margin-top: 2px;">(${totalCompletedHours}h Done · ${rosterBookedHoursTotal}h Booked)</div>
              </div>
              <div class="metric-card warning">
                  <div class="metric-title">Test Ready</div>
                  <div class="metric-value">${testReadyCount} Students</div>
              </div>
              <div class="metric-card" style="background: #ffffff; color: #0f172a; border: 1px solid #cbd5e1;">
                  <div class="metric-title">Passed Roster</div>
                  <div class="metric-value">${passedCount} Passed</div>
              </div>
          </div>

          <div class="section-card">
              <h3 class="section-title">🛡️ Instructor Compliance Record</h3>
              <table class="compliance-table">
                  <tr>
                      <td><b>Full Name:</b> ${instructorName || 'N/A'}</td>
                      <td><b>DVSA Badge Number:</b> ${instructorBadgeNumber || 'Pending'}</td>
                  </tr>
                  <tr>
                      <td><b>Date of Birth:</b> ${formattedDob}</td>
                      <td><b>Contact Email:</b> ${instructorEmail || 'N/A'}</td>
                  </tr>
              </table>
          </div>

          <div class="section-card">
              <h3 class="section-title">📊 Comprehensive Student Progress Breakdown</h3>
              <table class="roster">
                  <thead>
                      <tr>
                          <th>Student Name</th>
                          <th>Email Address</th>
                          <th>Training Category</th>
                          <th style="text-align: center;">Completed Hours</th>
                          <th style="text-align: center;">Booked Upcoming</th>
                          <th>Progress Status</th>
                      </tr>
                  </thead>
                  <tbody>
                      ${learners.length === 0 ? '<tr><td colspan="6" style="text-align:center; padding: 30px; color: #64748b;">No active students registered in your fleet.</td></tr>' : 
                          learners.map(l => {
                              const studentLogs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(l.id, 10));
                              const doneMinutes = studentLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
                              const doneHours = Math.round(doneMinutes / 60);

                              const studentBookings = instructorBookings.filter(b => 
                                  (parseInt(b.learner_id, 10) === parseInt(l.id, 10) || b.learner_name?.toLowerCase() === l.name?.toLowerCase()) &&
                                  b.status === 'confirmed' &&
                                  String(b.booking_date).slice(0, 10) >= todayStr
                              );
                              const bookedHours = studentBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

                              const statusClass = l.status === 'Passed' ? 'badge-passed' :
                                              l.status === 'Test Ready' ? 'badge-ready' :
                                              l.status === 'Intermediate' ? 'badge-intermediate' : 'badge-beginner';

                              return `
                                  <tr>
                                      <td><b>${l.name}</b></td>
                                      <td style="color: #64748b;">${l.email || 'N/A'}</td>
                                      <td>${l.training_category || 'Car (Manual)'}</td>
                                      <td style="text-align: center; color: #059669; font-weight: 800;">${doneHours}h</td>
                                      <td style="text-align: center; color: #2563eb; font-weight: 800;">${bookedHours}h</td>
                                      <td><span class="badge ${statusClass}">${l.status || 'Beginner'}</span></td>
                                  </tr>
                              `;
                          }).join('')
                      }
                  </tbody>
              </table>
          </div>

          <div class="footer">
              <p>LessonLane Instructor Management System · Confidential Operational Summary Roster</p>
          </div>
          <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
  };

  const exportAllStudentsWorkbook = () => {
    if (typeof XLSX === 'undefined') {
      alert("❌ XLSX library not loaded. Please ensure xlsx is included in your project dependencies.");
      return;
    }

    const todayStr = new Date().toISOString().slice(0, 10);
    const wb = XLSX.utils.book_new();

    const totalActiveStudents = learners.length;
    const totalCompletedHoursAll = learners.reduce((acc, l) => {
      const logs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(l.id, 10));
      const mins = logs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
      return acc + Math.round(mins / 60);
    }, 0);

    const futureBookingsAll = instructorBookings.filter(b => b.status === 'confirmed' && String(b.booking_date).slice(0, 10) >= todayStr);
    const futureHoursAll = futureBookingsAll.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

    const businessOverviewData = [
      ["LessonLane Business & Fleet Overview", ""],
      ["Metric", "Value"],
      ["Active Students", totalActiveStudents],
      ["Completed Teaching (Hours)", `${totalCompletedHoursAll}h`],
      ["Future Bookings (Hours)", `${futureHoursAll}h`],
      ["Total Lessons Logged", allLogsCache.length],
      ["Active Bookings Count", futureBookingsAll.length],
      ["Report Generated Date", new Date().toLocaleDateString('en-GB')]
    ];
    const wsOverview = XLSX.utils.aoa_to_sheet(businessOverviewData);
    XLSX.utils.book_append_sheet(wb, wsOverview, "Business Overview");

    const registerHeaders = ["Student Name", "Telephone", "Email Address", "Postcode", "Training Category", "Date Joined", "Completed Lessons", "Total Hours Completed", "Future Hours Booked", "Current Status", "Last Lesson Date"];
    const registerRows = learners.map(l => {
      const studentLogs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(l.id, 10));
      const doneMinutes = studentLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
      const doneHours = Math.round(doneMinutes / 60);

      const studentBookings = instructorBookings.filter(b => 
        (parseInt(b.learner_id, 10) === parseInt(l.id, 10) || b.learner_name?.toLowerCase() === l.name?.toLowerCase()) &&
        b.status === 'confirmed' &&
        String(b.booking_date).slice(0, 10) >= todayStr
      );
      const bookedHours = studentBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

      const sortedLogs = studentLogs.sort((a, b) => new Date(b.lesson_date) - new Date(a.lesson_date));
      const lastLesson = sortedLogs.length > 0 ? sortedLogs[0].lesson_date?.slice(0, 10) : 'None';

      const rawPhone = l.phone_number || l.phone || 'N/A';
      const addressPostcode = l.default_pickup_address || l.address || '';
      const postcode = addressPostcode ? addressPostcode.split(',').pop().trim() : 'N/A';

      return [
        l.name,
        rawPhone,
        l.email || 'N/A',
        postcode,
        l.training_category || 'Car (Manual)',
        l.created_at ? l.created_at.slice(0, 10) : 'N/A',
        studentLogs.length,
        `${doneHours}h`,
        `${bookedHours}h`,
        l.status || 'Beginner',
        lastLesson
      ];
    });
    const wsRegister = XLSX.utils.aoa_to_sheet([registerHeaders, ...registerRows]);
    XLSX.utils.book_append_sheet(wb, wsRegister, "Student Register");

    const historyHeaders = ["Student Name", "Lesson Date", "Duration (Mins)", "Progress Score (1-5)", "Instructor Notes", "Areas Needing Improvement", "Next Steps Plan"];
    const historyRows = [];
    allLogsCache.forEach(log => {
      const student = learners.find(l => parseInt(l.id, 10) === parseInt(log.learner_id));
      historyRows.push([
        student ? student.name : `Student #${log.learner_id}`,
        log.lesson_date ? log.lesson_date.slice(0, 10) : 'N/A',
        log.duration_minutes || 60,
        log.progress_score || 'N/A',
        log.custom_notes || 'N/A',
        log.areas_to_improve || 'N/A',
        log.next_steps || 'N/A'
      ]);
    });
    const wsHistory = XLSX.utils.aoa_to_sheet([historyHeaders, ...historyRows]);
    XLSX.utils.book_append_sheet(wb, wsHistory, "Lesson History");

    const bookingHeaders = ["Student Name", "Booking Date", "Start Time", "Duration (Hours)", "Pickup Location", "Booking Status"];
    const bookingRows = instructorBookings
      .filter(b => b.status === 'confirmed' && String(b.booking_date).slice(0, 10) >= todayStr)
      .map(b => [
        b.learner_name || 'Student',
        b.booking_date ? String(b.booking_date).slice(0, 10) : 'N/A',
        b.booking_time || 'N/A',
        b.duration || 1,
        b.exact_pickup_address || b.pickup_location || 'N/A',
        b.status
      ]);
    const wsBookings = XLSX.utils.aoa_to_sheet([bookingHeaders, ...bookingRows]);
    XLSX.utils.book_append_sheet(wb, wsBookings, "Upcoming Bookings");

    const perfHeaders = ["Student Name", "Training Category", "Overall Syllabus Mastery (%)", "Readiness Checklist Items Completed", "Current Status"];
    const perfRows = learners.map(l => {
      const targetFramework = DYNAMIC_VEHICLE_FRAMEWORKS[l.training_category || "Car (Manual)"];
      const totalSkillsCount = Object.values(targetFramework || {}).flat().length || 1;
      const safeSkills = l.skills || {};
      const totalAccumulatedScore = Object.values(safeSkills).reduce((sum, current) => sum + current, 0);
      const masteryPercent = Math.round((totalAccumulatedScore / (totalSkillsCount * 5)) * 100) || 0;

      let checklistCount = 0;
      if (l.test_readiness_checklist) {
        const checkArray = typeof l.test_readiness_checklist === 'string' ? JSON.parse(l.test_readiness_checklist) : l.test_readiness_checklist;
        checklistCount = Array.isArray(checkArray) ? checkArray.length : 0;
      }

      return [
        l.name,
        l.training_category || 'Car (Manual)',
        `${masteryPercent}%`,
        `${checklistCount} / ${MASTER_READINESS_CHECKLIST.length}`,
        l.status || 'Beginner'
      ];
    });
    const wsPerf = XLSX.utils.aoa_to_sheet([perfHeaders, ...perfRows]);
    XLSX.utils.book_append_sheet(wb, wsPerf, "Student Performance");

    XLSX.writeFile(wb, `LessonLane_Instructor_Workbook_${todayStr}.xlsx`);
  };

  return (
    <div style={{ ...Styles.containerStyle, padding: '0 0 50px 0', overflowX: 'hidden', background: '#0f172a', color: '#ffffff', minHeight: '100vh' }}>
      
      {/* --- MINIMALIST APP HEADER --- */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: '#090d16', borderBottom: '1px solid rgba(255,255,255,0.06)', position: 'sticky', top: 0, zIndex: 1000 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src={schoolLogo} alt="Logo" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #3b82f6' }} />
          <div>
            <div style={{ color: '#fff', fontWeight: '800', fontSize: '1.05rem', lineHeight: '1.2' }}>LessonLane</div>
            <div style={{ color: '#94a3b8', fontSize: '0.7rem', lineHeight: '1.2' }}>
              {instructorName || 'Instructor'} · <span style={{ color: '#34d399', fontWeight: '600' }}>Active: {lastLoginTime}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {activeMobileView !== 'menu' && (
            <button 
              onClick={() => setActiveMobileView('menu')} 
              title="Return to Menu"
              style={{ background: '#162238', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', width: '38px', height: '38px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
            </button>
          )}

          <button 
            onClick={() => setShowSettingsDrawer(true)} 
            title="Settings"
            style={{ background: '#162238', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', width: '38px', height: '38px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
          </button>

          <button 
            onClick={() => { localStorage.clear(); navigate('/'); }} 
            title="Logout"
            style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#fca5a5', width: '38px', height: '38px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
          </button>
        </div>
      </div>


{/* --- SHARP FRONT-PAGE MENU & INFORMATIVE FEEDS (Shown only on main menu) --- */}
      {activeMobileView === 'menu' && (
        <div style={{ padding: '20px 16px 10px 16px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
          
          {/* Top 2x2 Grid for Primary Features */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            
            {/* 1. Calendar & Hours */}
            <div 
              onClick={() => setActiveMobileView('calendar')} 
              style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', padding: '18px 14px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 8px 20px rgba(37, 99, 235, 0.3)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>📅</div>
              <div>
                <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem' }}>Calendar & Hours</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Schedule & booking slots</div>
              </div>
            </div>

            {/* 2. Students */}
            <div 
              onClick={() => setActiveMobileView('roster')} 
              style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', padding: '18px 14px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 8px 20px rgba(5, 150, 105, 0.3)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>👥</div>
              <div>
                <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem' }}>Students ({learners.length})</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Manage roster & progress</div>
              </div>
            </div>

            {/* 3. Messages */}
            <div 
              onClick={() => setActiveMobileView('messages')} 
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', padding: '18px 14px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 8px 20px rgba(124, 58, 237, 0.3)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>💬</div>
              <div>
                <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem' }}>Messages</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Inbox & broadcasts</div>
              </div>
            </div>

            {/* 4. Print Reports (Swapped into the upper right position) */}
            <div 
              onClick={() => setActiveMobileView('reports')} 
              style={{ background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)', padding: '18px 14px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 8px 20px rgba(217, 119, 6, 0.3)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>📊</div>
              <div>
                <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem' }}>Print Reports</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Summaries & Excel workbook</div>
              </div>
            </div>

          </div>

      {/* Bottom Row (Route Studio and Weekly Snapshot side-by-side) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            
            {/* Route Studio Tile */}
            <div 
              onClick={() => setActiveMobileView('routestudio')} 
              style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', padding: '16px 14px', borderRadius: '16px', textAlign: 'center', boxShadow: '0 8px 20px rgba(2, 132, 199, 0.35)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', fontSize: '18px' }}>🗺️</div>
              <div>
                <div style={{ fontWeight: '800', color: '#ffffff', fontSize: '0.95rem' }}>Route Studio</div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.85)', marginTop: '3px' }}>Interactive maps & waypoints</div>
              </div>
            </div>

            {/* Weekly Snapshot Card */}
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', padding: '16px 14px', boxShadow: '0 8px 20px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '0.8rem', color: '#60a5fa', fontWeight: '800' }}>📊 SNAPSHOT</h4>
                <span style={{ fontSize: '0.65rem', color: '#94a3b8', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: '4px' }}>Past/Future</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ background: 'rgba(5, 150, 105, 0.1)', border: '1px solid rgba(5, 150, 105, 0.25)', borderRadius: '8px', padding: '8px' }}>
                  <div style={{ fontSize: '0.65rem', color: '#34d399', fontWeight: '800' }}>⏪ PAST: {weeklyAnalytics.pastHours}H</div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{weeklyAnalytics.pastCount} lessons logged</div>
                </div>

                <div style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.25)', borderRadius: '8px', padding: '8px' }}>
                  <div style={{ fontSize: '0.65rem', color: '#60a5fa', fontWeight: '800' }}>⏩ AHEAD: {weeklyAnalytics.upcomingHours}H</div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{weeklyAnalytics.upcomingCount} lessons booked</div>
                </div>
              </div>
            </div>

          </div>



{/* ⚠️ Unlogged Past Lessons Alert Feed */}
          {unloggedPastLessons.length > 0 && (
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '16px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#fca5a5', fontWeight: '800' }}>⚠️ Unlogged Past Lessons ({unloggedPastLessons.length})</h4>
                <span style={{ fontSize: '0.68rem', color: '#f87171', background: 'rgba(239, 68, 68, 0.15)', padding: '2px 8px', borderRadius: '6px' }}>Requires Audit</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {unloggedPastLessons.map((b, idx) => (
                  <div key={idx} style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', color: '#fff', fontSize: '0.8rem' }}>🎓 {b.learner_name}</div>
                      {/* 🇬🇧 Formatted UK Date here */}
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>📅 {formatUKDate(b.booking_date || b.date)} at {b.booking_time}</div>
                    </div>
                    <button 
                      onClick={() => handleOpenTrackingPortal(b, parseInt(b.duration || 1, 10) * 60, String(b.booking_date || b.date).slice(0, 10))}
                      style={{ background: '#059669', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      📝 Log Now
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 📅 Next 7 Days Lessons Feed */}
          <div style={{ background: '#162238', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#60a5fa', fontWeight: '800' }}>🗓️ Next 7 Days Schedule ({upcomingLessonsNext7Days.length})</h4>
              <span style={{ fontSize: '0.7rem', color: '#94a3b8', background: 'rgba(255,255,255,0.05)', padding: '3px 8px', borderRadius: '6px' }}>Upcoming</span>
            </div>

            {upcomingLessonsNext7Days.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '0.75rem', fontStyle: 'italic', margin: 0, textAlign: 'center', padding: '10px' }}>No lessons booked for the next 7 days.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                {upcomingLessonsNext7Days.map((b, idx) => (
                  <div key={idx} style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', color: '#e2e8f0', fontSize: '0.8rem' }}>🎓 {b.learner_name}</div>
                      {/* 🇬🇧 Formatted UK Date here */}
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>📅 {formatUKDate(b.booking_date || b.date)} · 🕒 {b.booking_time} ({b.duration}h)</div>
                    </div>
                    <span style={{ fontSize: '0.68rem', background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', padding: '3px 8px', borderRadius: '6px', fontWeight: 'bold' }}>Confirmed</span>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}


      {/* --- FOCUSED CONTENT VIEW --- */}
      <div style={{ padding: '16px' }}>


{/* 🗺️ Route Studio View */}
        {activeMobileView === 'routestudio' && (
          <RouteStudio instructorId={instructorId} learners={learners} />
        )}


{/* VIEW 1: CALENDAR & WORKING HOURS */}
        {activeMobileView === 'calendar' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', position: 'relative', minHeight: '80vh', paddingBottom: '70px' }}>

            <div style={{ background: '#162238', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
              <div 
                onClick={() => setShowHoursAccordion(!showHoursAccordion)}
                style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: 'rgba(255,255,255,0.03)' }}
              >
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#ffffff' }}>🕒 Working Hours & Notice Policy</span>
                <span style={{ fontSize: '0.8rem', color: '#60a5fa', fontWeight: 'bold' }}>{showHoursAccordion ? '▲ Hide' : '▼ Configure'}</span>
              </div>

              {showHoursAccordion && (
                <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.08)', background: '#0f172a' }}>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.7rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>START:</label>
                      <select value={workStart} onChange={e => setWorkStart(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '12px' }}>
                        {Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`).map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.7rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>END:</label>
                      <select value={workEnd} onChange={e => setWorkEnd(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '12px' }}>
                        {Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`).map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '0.7rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>MIN CANCELLATION NOTICE:</label>
                    <select value={cancellationLockHours} onChange={e => setCancellationLockHours(parseInt(e.target.value, 10))} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '12px' }}>
                      <option value="0">Immediate</option>
                      <option value="24">24 Hours Notice</option>
                      <option value="48">48 Hours Notice</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '0.7rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>⏳ LESSON BUFFER:</label>
                    <select value={lessonBufferMinutes} onChange={e => setLessonBufferMinutes(parseInt(e.target.value, 10))} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '12px' }}>
                      <option value={0}>None (0m)</option>
                      <option value={15}>15 Minutes</option>
                      <option value={30}>30 Minutes</option>
                      <option value={45}>45 Minutes</option>
                      <option value={60}>1 Hour</option>
                    </select>
                  </div>
                  <button onClick={triggerMasterConfigurationSave} style={{ width: '100%', background: '#3b82f6', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)' }}>
                    💾 Save Hours & Policy
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', background: '#090d16', padding: '4px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
              {[
                { id: 'day', label: 'Day' },
                { id: '3days', label: '3 Days' },
                { id: 'week', label: 'Week' }
              ].map(v => (
                <button
                  key={v.id}
                  onClick={() => setCalendarView(v.id)}
                  style={{
                    flex: 1,
                    background: calendarView === v.id ? '#3b82f6' : 'transparent',
                    color: calendarView === v.id ? '#ffffff' : '#94a3b8',
                    border: 'none',
                    padding: '8px 0',
                    borderRadius: '9px',
                    fontWeight: 'bold',
                    fontSize: '11px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {v.label}
                </button>
              ))}
            </div>

            <div style={{ background: '#162238', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', padding: '16px', position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div 
                  onClick={() => setShowMonthPicker(!showMonthPicker)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <h3 style={{ margin: 0, color: '#ffffff', fontSize: '1rem', fontWeight: '800' }}>
                    {displayedDates.length > 0 ? displayedDates[0].toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'Schedule'}
                  </h3>
                  <span style={{ fontSize: '0.75rem', color: '#60a5fa', fontWeight: 'bold' }}>▼</span>
                </div>

                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={handlePrevPeriod} style={Styles.navBtn}>◀</button>
                  <button onClick={() => setCurrentDate(new Date())} style={{ ...Styles.navBtn, padding: '4px 10px', fontSize: '11px' }}>Today</button>
                  <button onClick={handleNextPeriod} style={Styles.navBtn}>▶</button>
                </div>
              </div>

              {showMonthPicker && (
                <div style={{
                  position: 'absolute',
                  top: '65px',
                  left: '16px',
                  right: '16px',
                  background: '#090d16',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '14px',
                  padding: '16px',
                  zIndex: 100,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.6)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#ffffff' }}>Choose month</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button 
                        onClick={() => {
                          const d = new Date(currentDate);
                          d.setFullYear(d.getFullYear() - 1);
                          setCurrentDate(d);
                        }}
                        style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                      >
                        ❮
                      </button>
                      <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#60a5fa' }}>
                        {displayedDates.length > 0 ? displayedDates[0].getFullYear() : new Date().getFullYear()}
                      </span>
                      <button 
                        onClick={() => {
                          const d = new Date(currentDate);
                          d.setFullYear(d.getFullYear() + 1);
                          setCurrentDate(d);
                        }}
                        style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                      >
                        ❯
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                    {[
                      { name: 'Jan', monthIdx: 0 }, { name: 'Feb', monthIdx: 1 }, { name: 'Mar', monthIdx: 2 },
                      { name: 'Apr', monthIdx: 3 }, { name: 'May', monthIdx: 4 }, { name: 'Jun', monthIdx: 5 },
                      { name: 'Jul', monthIdx: 6 }, { name: 'Aug', monthIdx: 7 }, { name: 'Sep', monthIdx: 8 },
                      { name: 'Oct', monthIdx: 9 }, { name: 'Nov', monthIdx: 10 }, { name: 'Dec', monthIdx: 11 }
                    ].map(m => {
                      const currentSelectedMonth = displayedDates.length > 0 ? displayedDates[0].getMonth() : new Date().getMonth();
                      const isSelected = currentSelectedMonth === m.monthIdx;
                      return (
                        <button
                          key={m.name}
                          onClick={() => {
                            const targetDate = displayedDates.length > 0 ? new Date(displayedDates[0]) : new Date();
                            targetDate.setMonth(m.monthIdx);
                            setCurrentDate(targetDate);
                            setShowMonthPicker(false);
                          }}
                          style={{
                            background: isSelected ? '#3b82f6' : '#162238',
                            color: '#fff',
                            border: isSelected ? '1px solid #60a5fa' : '1px solid rgba(255,255,255,0.08)',
                            padding: '12px',
                            borderRadius: '10px',
                            fontWeight: 'bold',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            textAlign: 'center',
                            boxShadow: isSelected ? '0 4px 12px rgba(59,130,246,0.3)' : 'none'
                          }}
                        >
                          {m.name}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center' }}>
                    Select a month to jump straight to it.
                  </div>
                </div>
              )}
              
              <div style={{ overflowX: 'auto' }}>
                <BookingCalendar 
                  instructor={instructorId}
                  template={template}
                  instructorBookings={instructorBookings}
                  timeLabels={timeLabels}
                  weekDates={displayedDates}
                  learners={learners}
                  role="instructor"
                  workStart={workStart}
                  workEnd={workEnd}
                  allLogsCache={allLogsCache}
                  onBookingSuccess={fetchData}
                  isMobile={true}
                  onCellClick={(date, time, isStudentReserved, liveBooking, isLessonInPast, hasLessonBeenLogged, isBlockedCellClick) => {
                    if (isBlockedCellClick) {
                      if (window.confirm(`Remove time-off block for ${date.toLocaleDateString()} at ${time}?`)) {
                        handleRemoveBlock(date, time);
                      }
                      return;
                    }

                    if (isStudentReserved && liveBooking) {
                      if (isLessonInPast) {
                        if (hasLessonBeenLogged) {
                          alert("🔒 This past lesson has already been logged and is locked for auditing.");
                          return;
                        }

                        const matchedStudent = learners.find(l => 
                          parseInt(l.id, 10) === parseInt(liveBooking.learner_id, 10) ||
                          l.name?.toLowerCase().trim() === liveBooking.learner_name?.toLowerCase().trim()
                        );

                        if (!matchedStudent) {
                          alert("❌ Error: Could not find matching student record for this booking.");
                          return;
                        }

                        const exactBookingDate = liveBooking.booking_date 
                          ? String(liveBooking.booking_date).slice(0, 10) 
                          : date.toISOString().slice(0, 10);
                          
                        const bookingDurationMins = liveBooking.duration ? parseInt(liveBooking.duration, 10) * 60 : 60;
                        handleOpenTrackingPortal(matchedStudent, bookingDurationMins, exactBookingDate);
                        return;
                      }

                      setSelectedBooking(liveBooking);
                      setActionType('view');
                      setMessageText(`Hi ${liveBooking.learner_name}, regarding your lesson on ${liveBooking.booking_date?.slice(0, 10)} at ${liveBooking.booking_time}: `);
                      setShowManageModal(true);
                    } else {
                      const [h, m] = time.split(':').map(Number);
                      const startMins = h * 60 + (m || 0);
                      const endMins = startMins + 60;
                      const endH = Math.floor(endMins / 60).toString().padStart(2, '0');
                      const endMin = (endMins % 60).toString().padStart(2, '0');

                      setSelection({ date, startTime: time, endTime: `${endH}:${endMin}` });
                      setSlotSelectedLearnerId(learners[0]?.id || '');
                      setSlotDurationHours(1);
                      setShowSlotModal(true);
                    }
                  }}
                />
              </div>
            </div>

       {/* ➕ Floating Action Button (FAB) for Quick Add */}
            <button
              onClick={() => {
                setQuickAddDate(new Date().toISOString().slice(0, 10));
                setQuickAddTime('09:00');
                setShowQuickAddModal(true);
              }}
              title="Add Lesson or Time-Off"
              style={{
                position: 'fixed',
                bottom: '24px',
                right: '20px',
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: '#ffffff',
                border: 'none',
                fontSize: '28px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                cursor: 'pointer',
                zIndex: 999,
                transition: 'transform 0.2s ease'
              }}
            >
              +
            </button>

          </div>
        )}

        {activeMobileView === 'roster' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ background: '#162238', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', padding: '16px' }}>
              <button 
                onClick={() => setShowAddStudentModal(!showAddStudentModal)}
                style={{ width: '100%', background: '#059669', color: '#fff', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)' }}
              >
                {showAddStudentModal ? '✕ Close Add Form' : '➕ Add New Student'}
              </button>

              {showAddStudentModal && (
                <div style={{ background: '#0f172a', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', marginTop: '12px' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#ffffff' }}>Register New Student</h4>
                  <form onSubmit={(e) => {
                    handleAddStudent(e);
                    setShowAddStudentModal(false);
                  }} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <input placeholder="Full Name" value={studentName} onChange={e => setStudentName(e.target.value)} required style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '13px' }} />
                    <input type="email" placeholder="Email Address" value={studentEmail} onChange={e => setStudentEmail(e.target.value)} required style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '13px' }} />
                    <select value={trainingCategory} onChange={e => setTrainingCategory(e.target.value)} style={{ padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '13px' }}>
                      {(vehicleCategories && vehicleCategories.length > 0 ? vehicleCategories : ["Car (Manual)"]).map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <button type="submit" style={{ background: '#059669', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>
                      Register & Send Setup Link 🚀
                    </button>
                  </form>
                </div>
              )}
            </div>

            <div style={{ background: '#162238', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', padding: '16px' }}>
              <h3 style={{ fontSize: '1rem', color: '#ffffff', marginBottom: '14px', fontWeight: '800' }}>Registered Students ({learners.length})</h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {learners.map(l => {
                  const progressPercent = calculateProgress(l.skills);
                  const studentLogs = allLogsCache.filter(log => parseInt(log.learner_id, 10) === parseInt(l.id, 10));
                  const doneMinutes = studentLogs.reduce((sum, log) => sum + (log.duration_minutes || (log.duration ? log.duration * 60 : 60)), 0);
                  const doneHours = Math.round(doneMinutes / 60);

                  const todayStr = new Date().toISOString().slice(0, 10);
                  const studentBookings = instructorBookings.filter(b => 
                    (parseInt(b.learner_id, 10) === parseInt(l.id, 10) || b.learner_name?.toLowerCase() === l.name?.toLowerCase()) &&
                    b.status === 'confirmed' &&
                    String(b.booking_date).slice(0, 10) >= todayStr
                  );
                  const bookedHours = studentBookings.reduce((sum, b) => sum + (parseInt(b.duration, 10) || 1), 0);

                  return (
                    <div key={l.id} style={{ background: '#0f172a', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <strong style={{ color: '#60a5fa', fontSize: '0.95rem', display: 'block' }}>{l.name}</strong>
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{l.email} • <span style={{ color: '#38bdf8', fontWeight: '600' }}>{l.training_category || "Car (Manual)"}</span></span>
                        </div>
                        
                        <select 
                          value={l.status || 'Beginner'} 
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            setLearners(prev => prev.map(item => item.id === l.id ? { ...item, status: newStatus } : item));
                            await fetch(`/api/learners/${l.id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }) });
                          }} 
                          style={{ background: l.status === 'Passed' ? 'rgba(34, 197, 94, 0.2)' : '#162238', color: l.status === 'Passed' ? '#4ade80' : '#ffffff', border: '1px solid rgba(255,255,255,0.15)', padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                        >
                          <option value="Beginner" style={{ background: '#0f172a' }}>Beginner</option>
                          <option value="Intermediate" style={{ background: '#0f172a' }}>Intermediate</option>
                          <option value="Test Ready" style={{ background: '#0f172a' }}>Test Ready</option>
                          <option value="Passed" style={{ background: '#0f172a' }}>Passed</option>
                        </select>
                      </div>

                      <div style={{ display: 'flex', gap: '15px', fontSize: '12px', marginBottom: '10px', background: '#162238', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div>
                          <span style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 'bold', display: 'block' }}>COMPLETED</span>
                          <strong style={{ color: '#34d399', fontSize: '0.85rem' }}>{doneHours} Hours</strong>
                        </div>
                        <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '15px' }}>
                          <span style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 'bold', display: 'block' }}>BOOKED UPCOMING</span>
                          <strong style={{ color: '#60a5fa', fontSize: '0.85rem' }}>{bookedHours} Hours</strong>
                        </div>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#94a3b8', marginBottom: '3px', fontWeight: 'bold' }}>
                          <span>Syllabus Progress</span>
                          <span>{progressPercent}%</span>
                        </div>
                        <div style={{ width: '100%', background: '#162238', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                          <div style={{ width: `${progressPercent}%`, backgroundColor: '#3b82f6', height: '100%', borderRadius: '4px' }}></div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <button 
                          onClick={() => {
                            setTargetMessageStudent(l);
                            setShowDirectMessageModal(true);
                          }} 
                          style={{ flex: 1, background: '#3b82f6', color: '#fff', border: 'none', padding: '8px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px', textAlign: 'center' }}
                        >
                          ✉️ Message
                        </button>
                        <button 
                          onClick={() => handleOpenStudentHub(l)} 
                          style={{ flex: 1, background: '#334155', color: '#fff', border: 'none', padding: '8px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '11px', textAlign: 'center' }}
                        >
                          📋 Track & Log
                        </button>
                        <button 
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to remove ${l.name}?`)) {
                              await fetch(`/api/learners/${l.id}`, { method: 'DELETE' });
                              fetchData();
                            }
                          }} 
                          style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                          title="Delete Student"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeMobileView === 'messages' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ background: '#162238', padding: '16px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '0.95rem', color: '#60a5fa', fontWeight: '800' }}>📢 Broadcast Announcement</h4>
              <form onSubmit={handleSendBroadcast} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <textarea rows={2} placeholder="Type broadcast message..." value={broadcastText} onChange={e => setBroadcastText(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }} />
                <button type="submit" disabled={isBroadcasting} style={{ background: '#059669', color: '#fff', border: 'none', padding: '8px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}>
                  {isBroadcasting ? 'Broadcasting...' : '📢 Send Broadcast'}
                </button>
              </form>
            </div>

            <div style={{ background: '#162238', padding: '16px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <h3 style={{ fontSize: '1rem', color: '#ffffff', marginBottom: '12px', fontWeight: '800' }}>Live Messages & Notices</h3>
              
              {dispatchMessages.length === 0 && adminMessages.length === 0 ? (
                <div style={{ background: '#0f172a', padding: '20px', textAlign: 'center', borderRadius: '12px', color: '#94a3b8', fontSize: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  No messages right now.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {adminMessages?.map(msg => (
                    <div key={`admin-${msg.id}`} style={{ background: 'rgba(5, 150, 105, 0.1)', border: '1px solid rgba(5, 150, 105, 0.3)', borderRadius: '12px', padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '12px', color: '#34d399' }}>👑 School Administration</strong>
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p style={{ fontSize: '12px', background: '#0f172a', padding: '8px', borderRadius: '8px', margin: '0 0 8px 0', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.08)' }}>
                        {msg.message_text}
                      </p>
                      <button 
                        onClick={async () => {
                          setAdminMessages(prev => prev.filter(m => m.id !== msg.id));
                          await fetch(`/api/messages/${msg.id}`, { method: 'DELETE' });
                          fetchData();
                        }}
                        style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.4)', padding: '6px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}
                      >
                        🗑️ Delete Notice
                      </button>
                    </div>
                  ))}

                  {Object.entries(
                    dispatchMessages.reduce((acc, msg) => {
                      const studentId = msg.learner_id;
                      if (!acc[studentId]) {
                        acc[studentId] = {
                          learnerName: msg.learner_name,
                          learnerId: studentId,
                          messages: []
                        };
                      }
                      acc[studentId].messages.push(msg);
                      return acc;
                    }, {})
                  ).map(([studentId, thread]) => (
                    <div key={`thread-${studentId}`} style={{ background: '#0f172a', padding: '14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '6px' }}>
                        <strong style={{ fontSize: '13px', color: '#ffffff' }}>🎓 {thread.learnerName}</strong>
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>{thread.messages.length} message(s)</span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '160px', overflowY: 'auto', marginBottom: '10px' }}>
                        {thread.messages.map((m) => {
                          const isInstructor = m.sender_role === 'instructor';
                          return (
                            <div key={m.id} style={{ 
                              background: isInstructor ? '#1e3a8a' : '#162238', 
                              padding: '8px 10px', 
                              borderRadius: '8px', 
                              alignSelf: isInstructor ? 'flex-end' : 'flex-start',
                              maxWidth: '85%',
                              border: '1px solid rgba(255,255,255,0.05)'
                            }}>
                              <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '2px' }}>
                                {isInstructor ? 'You' : thread.learnerName} • {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                              <p style={{ fontSize: '12px', margin: 0, color: '#e2e8f0', wordBreak: 'break-word' }}>
                                {m.message_text}
                              </p>
                            </div>
                          );
                        })}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input 
                            type="text" 
                            placeholder="Reply..." 
                            value={replyTexts[thread.learnerId] || ''} 
                            onChange={e => setReplyTexts({ ...replyTexts, [thread.learnerId]: e.target.value })}
                            style={{ flex: 1, padding: '7px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff' }}
                          />
                          <button onClick={() => handleSendInstructorReply(thread.learnerId)} style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '7px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>
                            Reply
                          </button>
                        </div>
                        <button 
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to delete the entire conversation thread with ${thread.learnerName}? This is an ultimate wipe and cannot be undone.`)) {
                              await fetch(`/api/messages/thread/${instructorId}/${thread.learnerId}`, { method: 'DELETE' });
                              fetchData();
                            }
                          }} 
                          style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.4)', padding: '7px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', textAlign: 'center' }}
                        >
                          🗑️ Delete Conversation                 
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeMobileView === 'reports' && (
          <div style={{ background: '#162238', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <h3 style={{ color: '#fbbf24', margin: '0 0 6px 0', fontSize: '1.1rem', fontWeight: '800' }}>Summary Reports</h3>
              <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0, lineHeight: '1.4' }}>
                Generate official summary printouts or export multi-tab student performance workbooks.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button 
                onClick={handleGenerateOverallFleetReport} 
                style={{ background: '#059669', color: '#fff', border: 'none', padding: '12px 16px', borderRadius: '10px', fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)' }}
              >
                🖨️ Print Summary Report
              </button>

              <button 
                onClick={exportAllStudentsWorkbook} 
                style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '12px 16px', borderRadius: '10px', fontWeight: 'bold', fontSize: '0.9rem', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}
              >
                📊 Export Student Workbook (.XLSX)
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Slot Booking / Blocking Modal */}
      {showSlotModal && selection && (
        <div style={Styles.modalOverlay}>
          <div style={{ background: '#162238', padding: '20px', borderRadius: '16px', width: '90%', maxWidth: '400px', border: '1.5px solid #3b82f6', color: '#fff', boxShadow: '0 15px 35px rgba(0,0,0,0.5)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '800' }}>Manage Slot: {selection.date.toLocaleDateString()} ({selection.startTime})</h3>
              <button onClick={() => setShowSlotModal(false)} style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
              <button type="button" onClick={() => setSlotIntent('book')} style={{ flex: 1, padding: '9px', background: slotIntent === 'book' ? '#1e293b' : '#0f172a', border: slotIntent === 'book' ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.15)', fontWeight: 'bold', fontSize: '12px', borderRadius: '8px', cursor: 'pointer', color: '#fff' }}>Book Lesson</button>
              <button type="button" onClick={() => setSlotIntent('block')} style={{ flex: 1, padding: '9px', background: slotIntent === 'block' ? '#1e293b' : '#0f172a', border: slotIntent === 'block' ? '2px solid #ef4444' : '1px solid rgba(255,255,255,0.15)', fontWeight: 'bold', fontSize: '12px', borderRadius: '8px', cursor: 'pointer', color: '#fff' }}>Block Time-Off</button>
            </div>

            <div style={{ background: '#0f172a', padding: '12px', borderRadius: '10px', marginBottom: '14px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>🕒 {slotIntent === 'book' ? 'LESSON' : 'BLOCK'} DURATION:</label>
              <select 
                value={slotDurationHours} 
                onChange={e => setSlotDurationHours(parseFloat(e.target.value))} 
                style={{ width: '100%', padding: '9px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '13px', background: '#162238', color: '#fff', marginBottom: '6px' }}
              >
                {slotIntent === 'book' ? (
                  <>
                    <option value={1}>1 Hour</option>
                    <option value={2}>2 Hours</option>
                    <option value={3}>3 Hours</option>
                    <option value={4}>4 Hours</option>
                  </>
                ) : (
                  <>
                    <option value={0.25}>15 Minutes</option>
                    <option value={0.5}>30 Minutes</option>
                    <option value={0.75}>45 Minutes</option>
                    <option value={1}>1 Hour</option>
                    <option value={1.5}>1 Hour 30 Minutes</option>
                    <option value={2}>2 Hours</option>
                  </>
                )}
              </select>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                <b>Time Range:</b> {selection.startTime} to {(() => {
                  const [h, m] = selection.startTime.split(':').map(Number);
                  const totalMins = (h * 60) + (m || 0) + Math.round(slotDurationHours * 60);
                  const endH = Math.floor(totalMins / 60).toString().padStart(2, '0');
                  const endMin = (totalMins % 60).toString().padStart(2, '0');
                  return `${endH}:${endMin}`;
                })()}
              </div>
            </div>

            {slotIntent === 'book' ? (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>🎓 DIRECT BOOK STUDENT:</label>
                  <select 
                    value={slotSelectedLearnerId} 
                    onChange={e => {
                      setSlotSelectedLearnerId(e.target.value);
                      const student = learners.find(l => parseInt(l.id, 10) === parseInt(e.target.value, 10));
                      if (student) {
                        setSlotPickupType('primary');
                        setSlotCustomPickup(student.default_pickup_address || student.address || '');
                      }
                    }} 
                    style={{ width: '100%', padding: '9px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '13px', background: '#0f172a', color: '#fff' }}
                  >
                    <option value="">-- Select Student --</option>
                    {learners.map(l => (
                      <option key={l.id} value={l.id}>{l.name} ({l.training_category || "Car"})</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>📍 PICKUP LOCATION CHOICE:</label>
                  <select 
                    value={slotPickupType} 
                    onChange={e => {
                      setSlotPickupType(e.target.value);
                      const student = learners.find(l => parseInt(l.id, 10) === parseInt(slotSelectedLearnerId, 10));
                      if (student) {
                        if (e.target.value === 'primary') {
                          setSlotCustomPickup(student.default_pickup_address || student.address || '');
                        } else if (e.target.value === 'secondary') {
                          setSlotCustomPickup(student.secondary_pickup || '');
                        }
                      }
                    }} 
                    style={{ width: '100%', padding: '9px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '13px', background: '#0f172a', color: '#fff', marginBottom: '8px' }}
                  >
                    <option value="primary">Primary Address (Home)</option>
                    <option value="secondary">Secondary Address (Work/Uni)</option>
                    <option value="custom">Custom / Other Address</option>
                  </select>
                  <input 
                    type="text"
                    value={slotCustomPickup}
                    onChange={e => setSlotCustomPickup(e.target.value)}
                    placeholder="Confirm or type exact address..."
                    style={{ width: '100%', padding: '9px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                  />
                </div>

                <button onClick={handleDirectInstructorBooking} style={{ width: '100%', background: '#3b82f6', color: '#fff', border: 'none', padding: '11px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}>
                  Confirm & Secure Booking ✅
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '14px', lineHeight: '1.4' }}>
                  Blocking this time range will prevent students from booking online.
                </p>
                <button onClick={() => handleSaveSlot('blocked')} style={{ width: '100%', background: '#ef4444', color: '#fff', border: 'none', padding: '11px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)' }}>
                  Apply Time-Off Lock 🛑
                </button>
              </>
            )}
          </div>
        </div>
      )}

{/* ➕ Quick Add Date & Time Picker Modal */}
      {showQuickAddModal && (
        <div style={Styles.modalOverlay}>
          <div style={{
            background: '#162238',
            padding: '20px',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '380px',
            maxHeight: '85vh',
            overflowY: 'auto',
            border: '1.5px solid #3b82f6',
            color: '#fff',
            boxShadow: '0 15px 35px rgba(0,0,0,0.5)',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ color: '#60a5fa', margin: 0, fontSize: '1rem', fontWeight: '800' }}>Add New Lesson / Slot</h3>
              <button onClick={() => setShowQuickAddModal(false)} style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>

            {/* SELECT NEW DATE PILLS */}
            <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.8px', color: '#94a3b8', marginBottom: '10px' }}>
              SELECT DATE
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '6px', marginBottom: '12px' }}>
              {Array.from({ length: 5 }, (_, i) => {
                const date = new Date();
                date.setDate(date.getDate() + i);

                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const value = `${year}-${month}-${day}`;
                const active = quickAddDate === value;

                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setQuickAddDate(value)}
                    style={{
                      minWidth: 0,
                      padding: '10px 2px',
                      borderRadius: '10px',
                      border: active ? '1px solid #60a5fa' : '1px solid #334155',
                      background: active ? '#2563eb' : '#0f172a',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <span style={{ fontSize: '0.65rem', color: active ? '#dbeafe' : '#94a3b8', fontWeight: 700 }}>
                      {i === 0 ? 'TODAY' : date.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase()}
                    </span>
                    <span style={{ fontSize: '1.05rem', fontWeight: 800 }}>{day}</span>
                    <span style={{ fontSize: '0.62rem', color: active ? '#dbeafe' : '#94a3b8' }}>
                      {date.toLocaleDateString('en-GB', { month: 'short' })}
                    </span>
                  </button>
                );
              })}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', padding: '11px 12px', marginBottom: '20px', cursor: 'pointer' }}>
              <span style={{ fontSize: '18px' }}>📅</span>
              <span style={{ flex: 1, color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 600 }}>Choose another date</span>
              <input
                type="date"
                value={quickAddDate}
                onChange={e => setQuickAddDate(e.target.value)}
                style={{ width: '36px', minWidth: 0, padding: '3px', border: 'none', background: 'transparent', color: '#fff', colorScheme: 'dark', cursor: 'pointer' }}
              />
            </label>

            {/* SELECT TIME GRID */}
            <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.8px', color: '#94a3b8', marginBottom: '10px' }}>
              SELECT TIME SLOT
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginBottom: '16px' }}>
              {Array.from({ length: 21 }, (_, i) => {
                const minutes = 8 * 60 + i * 30;
                const time = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
                const active = quickAddTime === time;

                return (
                  <button
                    key={time}
                    type="button"
                    onClick={() => setQuickAddTime(time)}
                    style={{
                      padding: '12px 4px',
                      borderRadius: '9px',
                      border: active ? '1px solid #60a5fa' : '1px solid #334155',
                      background: active ? '#2563eb' : '#0f172a',
                      color: active ? '#fff' : '#cbd5e1',
                      fontSize: '0.85rem',
                      fontWeight: active ? 800 : 600,
                      cursor: 'pointer'
                    }}
                  >
                    {time}
                  </button>
                );
              })}
            </div>

            {/* SELECTED SUMMARY & PROCEED BUTTON */}
            <div style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: '10px', padding: '12px', marginBottom: '14px' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 800, marginBottom: '5px' }}>
                SELECTED SLOT
              </div>
              <div style={{ color: '#f1f5f9', fontSize: '0.9rem', fontWeight: 800 }}>
                {quickAddDate ? new Date(`${quickAddDate}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'Choose date'}
                {' · '}
                {quickAddTime || 'Choose time'}
              </div>
            </div>

            <button
              onClick={() => {
                const [h, m] = quickAddTime.split(':').map(Number);
                const startMins = h * 60 + (m || 0);
                const endMins = startMins + 60;
                const endH = Math.floor(endMins / 60).toString().padStart(2, '0');
                const endMin = (endMins % 60).toString().padStart(2, '0');

                setSelection({
                  date: new Date(quickAddDate),
                  dateString: quickAddDate,
                  startTime: quickAddTime,
                  endTime: `${endH}:${endMin}`
                });
                setSlotSelectedLearnerId(learners[0]?.id || '');
                setSlotDurationHours(1);
                setSlotIntent('book');
                setShowQuickAddModal(false);
                setShowSlotModal(true);
              }}
              style={{
                width: '100%',
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                padding: '13px',
                borderRadius: '10px',
                fontWeight: 800,
                fontSize: '0.9rem',
                marginBottom: '8px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(59,130,246,0.2)'
              }}
            >
              Continue to Booking →
            </button>

            <button
              onClick={() => setShowQuickAddModal(false)}
              style={{ width: '100%', background: '#334155', color: '#e2e8f0', border: 'none', padding: '11px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}



      {/* Calendar Lesson Log Modal */}
      {selectedStudent && showLogLessonModal && (
        <div style={Styles.modalOverlay}>
          <div style={{ background: '#162238', padding: '18px', borderRadius: '16px', width: '94%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto', border: '1.5px solid #3b82f6', color: '#fff', boxSizing: 'border-box' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '10px' }}>
              <h3 style={{ margin: 0, color: '#60a5fa', fontSize: '1rem', fontWeight: '800' }}>📝 Log Lesson: {selectedStudent.name}</h3>
              <button onClick={() => setShowLogLessonModal(false)} style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>

            <div style={{ background: '#0f172a', padding: '10px 12px', borderRadius: '10px', marginBottom: '14px', fontSize: '0.8rem', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div>📅 <b style={{ color: '#fff' }}>Date:</b> {logDate}</div>
              <div>🕒 <b style={{ color: '#fff' }}>Duration:</b> {logDuration / 60} Hour(s)</div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>⭐ OVERALL SESSION GRADE (1-5):</label>
              <select 
                value={logProgressScore} 
                onChange={e => setLogProgressScore(parseInt(e.target.value, 10))} 
                style={{ width: '100%', padding: '9px', borderRadius: '8px', border: '1px solid #fbbf24', fontSize: '13px', background: '#0f172a', color: '#fff', fontWeight: 'bold' }}
                disabled={selectedStudent.isReadOnlyAudit}
              >
                {Object.entries(scoringGuide).map(([val, lbl]) => <option key={val} value={val}>{val} - {lbl}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#60a5fa', display: 'block', marginBottom: '8px' }}>🚗 DVSA CORE COMPETENCIES (1-5):</label>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '260px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)', padding: '10px', borderRadius: '10px', background: '#0f172a' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', alignItems: 'start' }}>
                  {Object.entries(DYNAMIC_VEHICLE_FRAMEWORKS[selectedStudent?.training_category || "Car (Manual)"] || {}).map(([category, skills]) => (
                    <div key={category} style={{ background: '#162238', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <strong style={{ fontSize: '0.7rem', color: '#60a5fa', display: 'block', marginBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '4px', fontWeight: '800' }}>
                        {category}
                      </strong>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {skills.map(skill => {
                          const currentScore = selectedStudent?.skills?.[skill] || 1;
                          return (
                            <div key={skill} style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '0.7rem' }}>
                              <span style={{ color: '#cbd5e1', fontWeight: '500', lineHeight: '1.2' }}>{skill}</span>
                              <div style={{ display: 'flex', gap: '3px' }}>
                                {[1, 2, 3, 4, 5].map(idx => {
                                  const active = idx <= currentScore;
                                  let bg = '#1e293b';
                                  let fg = '#94a3b8';
                                  if (active) {
                                    bg = currentScore === 5 ? '#22c55e' : currentScore >= 3 ? '#3b82f6' : '#ef4444';
                                    fg = '#ffffff';
                                  }
                                  return (
                                    <button
                                      key={idx}
                                      disabled={selectedStudent.isReadOnlyAudit}
                                      onClick={() => {
                                        if (selectedStudent.isReadOnlyAudit) return;
                                        const updated = { ...selectedStudent.skills, [skill]: idx };
                                        setSelectedStudent(prev => ({ ...prev, skills: updated }));
                                      }}
                                      style={{
                                        width: '22px', height: '22px', border: active ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.1)', borderRadius: '4px',
                                        background: bg, color: fg, fontSize: '10px', fontWeight: 'bold', cursor: selectedStudent.isReadOnlyAudit ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      {idx}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>SESSION NOTES:</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                {summaryCues.map((cue, idx) => (
                  <button 
                    key={idx}
                    type="button" 
                    onClick={() => setCustomLogNotes(prev => prev ? prev + " " + cue : cue)} 
                    style={{ fontSize: '10px', padding: '5px 10px', background: '#0f172a', border: '1px solid #3b82f6', color: '#60a5fa', borderRadius: '12px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    {cue}
                  </button>
                ))}
              </div>
              <textarea 
                rows={2}
                value={customLogNotes}
                onChange={e => setCustomLogNotes(e.target.value)}
                placeholder="Enter lesson summary..."
                style={{ width: '100%', padding: '9px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                disabled={selectedStudent.isReadOnlyAudit}
              />
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '5px' }}>NEXT STEPS / TARGETS:</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                {nextStepsCues.map((cue, idx) => (
                  <button 
                    key={idx}
                    type="button" 
                    onClick={() => setNextStepsPlan(prev => prev ? prev + " " + cue : cue)} 
                    style={{ fontSize: '10px', padding: '5px 10px', background: '#0f172a', border: '1px solid #059669', color: '#34d399', borderRadius: '12px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    {cue}
                  </button>
                ))}
              </div>
              <textarea 
                rows={2}
                value={nextStepsPlan}
                onChange={e => setNextStepsPlan(e.target.value)}
                placeholder="Next lesson objectives..."
                style={{ width: '100%', padding: '9px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }}
                disabled={selectedStudent.isReadOnlyAudit}
              />
            </div>

            {selectedStudent.isReadOnlyAudit ? (
              <button onClick={() => setSelectedStudent(prev => ({ ...prev, isReadOnlyAudit: false }))} style={{ width: '100%', background: '#d97706', color: '#fff', border: 'none', padding: '11px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}>
                🔓 Unlock to Edit Log
              </button>
            ) : (
              <button onClick={handleCommitCompleteLessonReport} style={{ width: '100%', background: '#3b82f6', color: '#fff', border: 'none', padding: '11px', borderRadius: '8px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}>
                {isSaving ? 'Saving...' : '💾 Save Lesson Log ✅'}
              </button>
            )}

          </div>
        </div>
      )}

      {/* Student Hub Modal */}
      {selectedStudent && showProgressModal && (
        <div style={Styles.modalOverlay}>
          <div style={{ background: '#162238', padding: '18px', borderRadius: '16px', width: '94%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto', border: '1.5px solid #3b82f6', color: '#fff', boxSizing: 'border-box' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '10px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#60a5fa', fontSize: '1rem', fontWeight: '800' }}>🎯 Student Hub: {selectedStudent.name}</h3>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{selectedStudent.training_category || "Car (Manual)"}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* 💾 Unified Save Button */}
                <button 
                  onClick={handleUnifiedSave}
                  disabled={!hasChanges}
                  title={hasChanges ? "Save Changes" : "No changes to save"}
                  style={{ 
                    background: hasChanges ? '#059669' : '#334155', 
                    color: hasChanges ? '#fff' : '#94a3b8', 
                    border: 'none', 
                    padding: '5px 10px', 
                    borderRadius: '6px', 
                    fontSize: '11px', 
                    fontWeight: 'bold', 
                    cursor: hasChanges ? 'pointer' : 'not-allowed',
                    opacity: hasChanges ? 1 : 0.6,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                >
                  💾 Save
                </button>

                <button 
                  onClick={handleGeneratePrintReport}
                  title="Print Student Report"
                  style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '5px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  🖨️ Print
                </button>
                <button onClick={() => setShowProgressModal(false)} style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#94a3b8', paddingLeft: '4px' }}>✕</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginBottom: '16px' }}>
              <button 
                onClick={() => setModalActiveTab('matrix')}
                style={{ padding: '8px 2px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer', background: modalActiveTab === 'matrix' ? '#3b82f6' : '#0f172a', color: '#fff' }}
              >
                🚗 Matrix
              </button>
              <button 
                onClick={() => setModalActiveTab('checklist')}
                style={{ padding: '8px 2px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer', background: modalActiveTab === 'checklist' ? '#3b82f6' : '#0f172a', color: '#fff' }}
              >
                📋 Checklist
              </button>
              <button 
                onClick={() => setModalActiveTab('logs')}
                style={{ padding: '8px 2px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer', background: modalActiveTab === 'logs' ? '#3b82f6' : '#0f172a', color: '#fff' }}
              >
                📜 Logs
              </button>
              <button 
                onClick={() => setModalActiveTab('profile')}
                style={{ padding: '8px 2px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer', background: modalActiveTab === 'profile' ? '#3b82f6' : '#0f172a', color: '#fff' }}
              >
                👤 Profile
              </button>
            </div>

            {modalActiveTab === 'matrix' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: 0 }}>Review or update student core competency levels (1-5):</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', maxHeight: '300px', overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)', padding: '10px', borderRadius: '10px', background: '#0f172a' }}>
                  {Object.entries(DYNAMIC_VEHICLE_FRAMEWORKS[selectedStudent?.training_category || "Car (Manual)"] || {}).map(([category, skills]) => (
                    <div key={category} style={{ background: '#162238', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <strong style={{ fontSize: '0.7rem', color: '#60a5fa', display: 'block', marginBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '4px', fontWeight: '800' }}>
                        {category}
                      </strong>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {skills.map(skill => {
                          const currentScore = selectedStudent?.skills?.[skill] || 1;
                          return (
                            <div key={skill} style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '0.7rem' }}>
                              <span style={{ color: '#cbd5e1', fontWeight: '500', lineHeight: '1.2' }}>{skill}</span>
                              <div style={{ display: 'flex', gap: '3px' }}>
                                {[1, 2, 3, 4, 5].map(idx => {
                                  const active = idx <= currentScore;
                                  let bg = '#1e293b';
                                  let fg = '#94a3b8';
                                  if (active) {
                                    bg = currentScore === 5 ? '#22c55e' : currentScore >= 3 ? '#3b82f6' : '#ef4444';
                                    fg = '#ffffff';
                                  }
                                  return (
                                    <button
                                      key={idx}
                                      onClick={() => {
                                        const updated = { ...selectedStudent.skills, [skill]: idx };
                                        setSelectedStudent(prev => ({ ...prev, skills: updated }));
                                        setHasChanges(true);
                                      }}
                                      style={{
                                        width: '22px', height: '22px', border: active ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.1)', borderRadius: '4px',
                                        background: bg, color: fg, fontSize: '10px', fontWeight: 'bold', cursor: 'pointer'
                                      }}
                                    >
                                      {idx}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {modalActiveTab === 'checklist' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.85rem', maxHeight: '340px', overflowY: 'auto' }}>
                <div>
                  <strong style={{ display: 'block', marginBottom: '6px', color: '#60a5fa', fontSize: '0.85rem' }}>📋 Test Readiness Master Checklist:</strong>
                  <p style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '10px' }}>Check off prerequisites, then click 💾 Save in the top header:</p>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {MASTER_READINESS_CHECKLIST.map(item => {
                      const ready = readinessChecklist.includes(item);
                      return (
                        <div 
                          key={item}
                          onClick={() => {
                            setReadinessChecklist(prev => 
                              prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
                            );
                            setHasChanges(true);
                          }}
                          style={{ display: 'flex', justifyContent: 'space-between', padding: '11px 14px', background: '#0f172a', borderRadius: '10px', border: ready ? '1px solid #059669' : '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', alignItems: 'center', fontSize: '0.8rem' }}
                        >
                          <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{item}</span>
                          <span style={{ fontSize: '1.1rem' }}>{ready ? '✅' : '⏳'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {modalActiveTab === 'logs' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.85rem', maxHeight: '340px', overflowY: 'auto' }}>
                <strong style={{ display: 'block', color: '#60a5fa', fontSize: '0.85rem' }}>📜 Past Lesson Audit Feed:</strong>
                {historicalLogs.length === 0 ? (
                  <div style={{ background: '#0f172a', padding: '20px', textAlign: 'center', borderRadius: '10px', color: '#94a3b8', fontSize: '0.75rem', fontStyle: 'italic', border: '1px solid rgba(255,255,255,0.08)' }}>
                    No historical logs recorded for this student yet.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {historicalLogs.map((log, i) => (
                      <div key={i} style={{ background: '#0f172a', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div style={{ fontWeight: 'bold', color: '#60a5fa', fontSize: '0.78rem', marginBottom: '4px' }}>📅 {log.lesson_date?.slice(0, 10)} (Grade: {log.progress_score}/5)</div>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#cbd5e1', wordBreak: 'break-word' }}>{log.custom_notes || 'No summary notes'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {modalActiveTab === 'profile' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.85rem', maxHeight: '340px', overflowY: 'auto' }}>
                <div style={{ background: '#0f172a', padding: '14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontWeight: 'bold', color: '#94a3b8', fontSize: '0.75rem' }}>NAME:</label>
                  <input type="text" value={selectedStudent.name || ''} onChange={e => { setSelectedStudent({...selectedStudent, name: e.target.value}); setHasChanges(true); }} style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '13px' }} />
                  
                  <label style={{ fontWeight: 'bold', color: '#94a3b8', fontSize: '0.75rem' }}>EMAIL:</label>
                  <input type="email" value={selectedStudent.email || ''} onChange={e => { setSelectedStudent({...selectedStudent, email: e.target.value}); setHasChanges(true); }} style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '13px' }} />

                  <label style={{ fontWeight: 'bold', color: '#94a3b8', fontSize: '0.75rem' }}>PHONE NUMBER:</label>
                  <input type="text" value={selectedStudent.phone_number || selectedStudent.phone || ''} onChange={e => { setSelectedStudent({...selectedStudent, phone_number: e.target.value}); setHasChanges(true); }} style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '13px' }} />

                  <label style={{ fontWeight: 'bold', color: '#94a3b8', fontSize: '0.75rem' }}>PRIMARY ADDRESS:</label>
                  <input type="text" value={selectedStudent.default_pickup_address || selectedStudent.address || ''} onChange={e => { setSelectedStudent({...selectedStudent, default_pickup_address: e.target.value}); setHasChanges(true); }} style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#162238', color: '#fff', fontSize: '13px' }} />
                </div>
              </div>
            )}

            <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '12px' }}>
              <button 
                onClick={() => setShowProgressModal(false)}
                style={{ width: '100%', background: '#334155', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer' }}
              >
                Close Student Hub
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Manage Existing Booking Modal */}
      {showManageModal && selectedBooking && (
        <div style={Styles.modalOverlay}>
          <div style={{ 
            background: '#162238', 
            padding: '20px', 
            borderRadius: '16px', 
            width: '90%', 
            maxWidth: '380px', 
            maxHeight: '85vh', 
            overflowY: 'auto',  
            border: '1.5px solid #3b82f6', 
            color: '#fff', 
            boxShadow: '0 15px 35px rgba(0,0,0,0.5)',
            boxSizing: 'border-box' 
          }}>
            <h3 style={{ color: '#60a5fa', marginTop: 0, fontSize: '1rem', fontWeight: '800' }}>Manage Lesson: {selectedBooking.learner_name}</h3>
            <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: '4px 0 14px 0' }}>📅 {new Date(selectedBooking.booking_date).toDateString()} at {selectedBooking.booking_time}</p>

            {actionType === 'move' && (
              <div style={{ margin: '14px 0 0', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.8px', color: '#94a3b8', marginBottom: '10px' }}>
                  SELECT NEW DATE
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '6px', marginBottom: '12px' }}>
                  {Array.from({ length: 5 }, (_, i) => {
                    const date = new Date();
                    date.setDate(date.getDate() + i);

                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const value = `${year}-${month}-${day}`;
                    const active = newBookingDate === value;

                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setNewBookingDate(value)}
                        style={{
                          minWidth: 0,
                          padding: '10px 2px',
                          borderRadius: '10px',
                          border: active ? '1px solid #60a5fa' : '1px solid #334155',
                          background: active ? '#2563eb' : '#0f172a',
                          color: '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '5px'
                        }}
                      >
                        <span style={{ fontSize: '0.65rem', color: active ? '#dbeafe' : '#94a3b8', fontWeight: 700 }}>
                          {i === 0 ? 'TODAY' : date.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase()}
                        </span>
                        <span style={{ fontSize: '1.05rem', fontWeight: 800 }}>{day}</span>
                        <span style={{ fontSize: '0.62rem', color: active ? '#dbeafe' : '#94a3b8' }}>
                          {date.toLocaleDateString('en-GB', { month: 'short' })}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', padding: '11px 12px', marginBottom: '20px', cursor: 'pointer' }}>
                  <span style={{ fontSize: '18px' }}>📅</span>
                  <span style={{ flex: 1, color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 600 }}>Choose another date</span>
                  <input
                    type="date"
                    value={newBookingDate}
                    min={(() => {
                      const d = new Date();
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    })()}
                    onChange={e => setNewBookingDate(e.target.value)}
                    style={{ width: '36px', minWidth: 0, padding: '3px', border: 'none', background: 'transparent', color: '#fff', colorScheme: 'dark', cursor: 'pointer' }}
                    aria-label="Choose a different lesson date"
                  />
                </label>

                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.8px', color: '#94a3b8', marginBottom: '10px' }}>
                  SELECT NEW TIME
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px', marginBottom: '16px' }}>
                  {Array.from({ length: 21 }, (_, i) => {
                    const minutes = 8 * 60 + i * 30;
                    const time = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
                    const active = newBookingTime === time;

                    return (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setNewBookingTime(time)}
                        aria-pressed={active}
                        style={{
                          padding: '12px 4px',
                          borderRadius: '9px',
                          border: active ? '1px solid #60a5fa' : '1px solid #334155',
                          background: active ? '#2563eb' : '#0f172a',
                          color: active ? '#fff' : '#cbd5e1',
                          fontSize: '0.85rem',
                          fontWeight: active ? 800 : 600,
                          cursor: 'pointer',
                          transition: 'background 0.15s ease'
                        }}
                      >
                        {time}
                      </button>
                    );
                  })}
                </div>

                <div style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(96,165,250,0.2)', borderRadius: '10px', padding: '12px', marginBottom: '14px' }}>
                  <div style={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 800, marginBottom: '5px' }}>
                    NEW LESSON TIME
                  </div>
                  <div style={{ color: '#f1f5f9', fontSize: '0.9rem', fontWeight: 800 }}>
                    {newBookingDate ? new Date(`${newBookingDate}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : 'Choose a date'}
                    {' · '}
                    {newBookingTime || 'Choose a time'}
                  </div>
                </div>

                <button
                  onClick={handleMoveBooking}
                  disabled={isProcessing || !newBookingDate || !newBookingTime}
                  style={{
                    width: '100%',
                    background: isProcessing || !newBookingDate || !newBookingTime ? '#334155' : '#3b82f6',
                    color: '#fff',
                    border: 'none',
                    padding: '13px',
                    borderRadius: '10px',
                    fontWeight: 800,
                    fontSize: '0.9rem',
                    marginBottom: '8px',
                    cursor: isProcessing || !newBookingDate || !newBookingTime ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(59,130,246,0.2)'
                  }}
                >
                  {isProcessing ? 'Saving changes...' : 'Confirm New Time →'}
                </button>

                <button
                  onClick={() => setActionType('view')}
                  disabled={isProcessing}
                  style={{ width: '100%', background: '#334155', color: '#e2e8f0', border: 'none', padding: '11px', borderRadius: '10px', fontWeight: 700, cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            )}

            {actionType === 'message' && (
              <div style={{ margin: '12px 0', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '12px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: '800', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>MESSAGE TO STUDENT:</label>
                <textarea rows={3} value={messageText} onChange={e => setMessageText(e.target.value)} style={{ width: '100%', padding: '9px', margin: '0 0 12px 0', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: '#0f172a', color: '#fff', fontSize: '12px', boxSizing: 'border-box' }} />
                <button onClick={handleSendMessage} style={{ width: '100%', background: '#059669', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', marginBottom: '8px', cursor: 'pointer' }}>Send Message</button>
                <button onClick={() => setActionType('view')} style={{ width: '100%', background: '#334155', color: '#fff', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              </div>
            )}

            {actionType === 'view' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                <button onClick={() => setActionType('move')} style={{ background: '#d97706', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>🔄 Reschedule / Move</button>
                <button onClick={() => setActionType('message')} style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>✉️ Message Student</button>
                <button onClick={() => handleCancelBooking(selectedBooking.id)} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}>❌ Cancel Booking</button>
                <button onClick={() => setShowManageModal(false)} style={{ background: '#334155', color: '#fff', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', marginTop: '4px' }}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Direct Message Modal */}
      {showDirectMessageModal && targetMessageStudent && (
        <div style={Styles.modalOverlay}>
          <div style={{ background: '#162238', padding: '20px', borderRadius: '16px', width: '90%', maxWidth: '380px', border: '1.5px solid #3b82f6', color: '#fff', boxShadow: '0 15px 35px rgba(0,0,0,0.5)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ color: '#60a5fa', margin: 0, fontSize: '1rem', fontWeight: '800' }}>✉️ Direct Message: {targetMessageStudent.name}</h3>
              <button onClick={() => { setShowDirectMessageModal(false); setTargetMessageStudent(null); setDirectMessageText(''); }} style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>
            <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '12px', lineHeight: '1.4' }}>
              Send a notification straight to this student's portal inbox.
            </p>
            <textarea 
              rows={4}
              value={directMessageText}
              onChange={e => setDirectMessageText(e.target.value)}
              placeholder="Type your message here..."
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '12px', marginBottom: '14px', background: '#0f172a', color: '#fff', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button 
                onClick={() => { setShowDirectMessageModal(false); setTargetMessageStudent(null); setDirectMessageText(''); }} 
                style={{ background: '#334155', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
              >
                Cancel
              </button>
              <button 
                onClick={handleSendDirectMessage}
                style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)' }}
              >
                Send Message 🚀
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettingsDrawer && (
        <SettingsDrawer 
          setShowSettingsDrawer={setShowSettingsDrawer}
          instructorEmail={instructorEmail}
          instructorPhone={instructorPhone}
          setInstructorPhone={setInstructorPhone}
          hqPostcode={hqPostcode}
          setHqPostcode={setHqPostcode}
          findNearbyAreas={findNearbyAreas}
          nearbyAreas={nearbyAreas}
          selectedAreas={selectedAreas}
          setSelectedAreas={setSelectedAreas}
          travelRadius={travelRadius}
          setTravelRadius={setTravelRadius}
          vehicleCategories={vehicleCategories}
          setVehicleCategories={setVehicleCategories}
          triggerMasterConfigurationSave={triggerMasterConfigurationSave}
          instructorDob={instructorDob}
          instructorBadgeNumber={instructorBadgeNumber}
          instructorId={instructorId}
          instructorName={instructorName}
          postcodeLatitude={postcodeLatitude}       
          postcodeLongitude={postcodeLongitude}
        />
      )}

      <InstructorHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />

    </div>
  );
}

export default InstructorDashMobile;